import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// Import services - use relative path for now, will be adjusted based on actual file location
import GradingService, { GradingSubmission } from '../../../platform/app/src/services/GradingService';
import ActivityService from '../../../platform/app/src/services/ActivityService';
import {
  extractAnnotationIdFromSaveResponse,
  fetchAnnotationIdForGrader,
  normalizeAnnotationId,
} from '../../../platform/app/src/utils/annotationIdFromResponse';
import SecureStorageService from '../../../platform/app/src/services/SecureStorageService';
import loadingGif from '../../../platform/app/assets/loading.gif';
import AppTooltip from '../../../platform/app/src/components/AppTooltip/AppTooltip';
import { TooltipProvider, useNotification } from '@ohif/ui-next';
import { useSystem } from '@ohif/core';
import { preserveQueryParameters } from '@ohif/app';
import { registerViewerBeforeUnloadGuard } from '../../../platform/app/src/utils/viewerBeforeUnloadGuard';
import {
  getActiveViewerStudyUid,
  getViewerStudyUidFromSearch,
  isAnnotationResponseForStudyUid,
} from '../../../platform/app/src/utils/viewerStudySession';
import { eventTarget } from '@cornerstonejs/core';
import type {
  Protocol,
  Parameter,
  ParameterField,
  AnnotationData,
  AnnotationResponse,
} from '../../../platform/app/src/services/AnnotationService';

// Extend Window interface to include annotationsData
declare global {
  interface Window {
    annotationsData?: AnnotationResponse;
    cornerstoneTools?: any;
    currentStudyUid?: string;
  }
}

/**
 * Plain decimal numeric input (digits, optional leading minus, one decimal point).
 * No scientific notation (e/E) or plus sign.
 *
 * Uses type="text" + validation: Firefox allows letters and exponent keys in type="number".
 */
const NUMERIC_DECIMAL_INPUT_REGEX = /^-?(\d+\.?\d*|\.\d*)$/;

const isAllowedNumericDecimalInput = (value: string): boolean =>
  value === '' || value === '-' || NUMERIC_DECIMAL_INPUT_REGEX.test(value);

/** Strip invalid characters (e/E, letters, +, etc.) from pasted or edited text. */
const sanitizeNumericDecimalInput = (raw: string): string => {
  let value = raw.replace(/[^\d.-]/g, '');
  const isNegative = value.startsWith('-');
  value = value.replace(/-/g, '');
  if (isNegative) {
    value = `-${value}`;
  }
  const dotIndex = value.indexOf('.');
  if (dotIndex !== -1) {
    value = value.slice(0, dotIndex + 1) + value.slice(dotIndex + 1).replace(/\./g, '');
  }
  return value;
};

const NUMERIC_DECIMAL_CONTROL_KEYS = new Set([
  'Backspace',
  'Delete',
  'Tab',
  'Escape',
  'Enter',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Home',
  'End',
]);

const createNumericDecimalInputHandlers = (
  onValueChange: (value: string) => void
): {
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => void;
} => ({
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (isAllowedNumericDecimalInput(inputValue)) {
      onValueChange(inputValue);
    }
  },
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey || e.metaKey || e.altKey) {
      return;
    }
    if (NUMERIC_DECIMAL_CONTROL_KEYS.has(e.key)) {
      return;
    }
    if (e.key === '-' || e.key === '.' || /^\d$/.test(e.key)) {
      return;
    }
    e.preventDefault();
  },
  onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = sanitizeNumericDecimalInput(e.clipboardData.getData('text/plain'));
    const input = e.currentTarget;
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    const merged = `${input.value.slice(0, start)}${pasted}${input.value.slice(end)}`;
    const sanitized = sanitizeNumericDecimalInput(merged);
    if (isAllowedNumericDecimalInput(sanitized)) {
      onValueChange(sanitized);
    }
  },
});

interface Position {
  x: number;
  y: number;
}

interface FloatingFormState {
  isVisible: boolean;
  instanceUid: string | null;
  showForm: (instanceUid?: string) => void;
  hideForm: () => void;
}

type SaveDraftHandlerOptions = {
  skipFormValidation?: boolean;
  fromNavigation?: boolean;
};

type SaveDraftHandler = (options?: SaveDraftHandlerOptions) => Promise<boolean>;

const serializeFormDataForCompare = (data: Record<string, any>): string => {
  const normalized: Record<string, unknown> = {};
  for (const key of Object.keys(data).sort()) {
    const value = data[key];
    if (Array.isArray(value)) {
      normalized[key] = value.map(v => String(v).trim()).filter(Boolean).sort();
    } else if (value != null && value !== '') {
      normalized[key] = String(value).trim();
    } else {
      normalized[key] = '';
    }
  }
  return JSON.stringify(normalized);
};

let floatingFormBaselineCaptured = false;

const captureFloatingFormBaseline = (data: Record<string, any>) => {
  const serialized = serializeFormDataForCompare(data);
  if (typeof window !== 'undefined') {
    (window as any).floatingFormDataBaseline = serialized;
    (window as any).floatingFormDataCurrent = serialized;
  }
  floatingFormBaselineCaptured = true;
};

const resetFloatingFormBaseline = () => {
  floatingFormBaselineCaptured = false;
  if (typeof window !== 'undefined') {
    (window as any).floatingFormDataBaseline = null;
    (window as any).floatingFormDataCurrent = null;
  }
};

type PendingNavigation = {
  onProceed: () => void | Promise<void>;
  resolve: () => void;
};

// Simple state management
const createFloatingFormStore = () => {
  let isVisible = false;
  let instanceUid: string | null = null;
  let listeners: (() => void)[] = [];
  let navigationListeners: (() => void)[] = [];
  let unsavedNavigationListeners: (() => void)[] = [];
  let shouldNavigateToStudiesList = false;
  let saveDraftHandler: SaveDraftHandler | null = null;
  let studyContextResetHandler: ((activeStudyUid: string) => void) | null = null;
  let pendingNavigation: PendingNavigation | null = null;
  let showUnsavedNavigationModal = false;
  let unsavedNavigationSaving = false;

  const subscribe = (listener: () => void) => {
    listeners.push(listener);
    return () => {
      listeners = listeners.filter(l => l !== listener);
    };
  };

  const subscribeToNavigation = (listener: () => void) => {
    navigationListeners.push(listener);
    return () => {
      navigationListeners = navigationListeners.filter(l => l !== listener);
    };
  };

  const subscribeToUnsavedNavigation = (listener: () => void) => {
    unsavedNavigationListeners.push(listener);
    return () => {
      unsavedNavigationListeners = unsavedNavigationListeners.filter(l => l !== listener);
    };
  };

  const notify = () => {
    listeners.forEach(listener => listener());
  };

  const notifyNavigation = () => {
    navigationListeners.forEach(listener => listener());
  };

  const notifyUnsavedNavigation = () => {
    unsavedNavigationListeners.forEach(listener => listener());
  };

  const logActivityAndProceed = async (onProceed: () => void | Promise<void>) => {
    const { logViewerStudyActivityOnLeave } = await import(
      '../../../extensions/cornerstone/src/utils/annotationSaveUtils'
    );
    await logViewerStudyActivityOnLeave();
    await onProceed();
  };

  const completePendingNavigation = async () => {
    const pending = pendingNavigation;
    pendingNavigation = null;
    showUnsavedNavigationModal = false;
    unsavedNavigationSaving = false;
    notifyUnsavedNavigation();

    if (!pending) {
      return;
    }

    try {
      await logActivityAndProceed(pending.onProceed);
    } finally {
      pending.resolve();
    }
  };

  const cancelPendingNavigation = () => {
    const pending = pendingNavigation;
    pendingNavigation = null;
    showUnsavedNavigationModal = false;
    unsavedNavigationSaving = false;
    notifyUnsavedNavigation();
    pending?.resolve();
  };

  const saveDraftAnnotationsImpl = async (): Promise<boolean> => {
    const { isLoggedInUserStudyGrader } = await import(
      '../../../platform/app/src/utils/graderStudyViewOnly'
    );

    if (!(await isLoggedInUserStudyGrader())) {
      return false;
    }

    try {
      if (saveDraftHandler) {
        return await saveDraftHandler({ skipFormValidation: true, fromNavigation: true });
      }

      const { saveDraftAnnotationsWithActivity } = await import(
        '../../../extensions/cornerstone/src/utils/annotationSaveUtils'
      );
      return await saveDraftAnnotationsWithActivity();
    } catch {
      return false;
    }
  };

  return {
    getState: () => ({ isVisible, instanceUid }),
    getUnsavedNavigationState: () => ({
      showModal: showUnsavedNavigationModal,
      isSaving: unsavedNavigationSaving,
    }),
    showForm: (uid?: string) => {
      isVisible = true;
      instanceUid = uid || null;
      notify();
    },
    hideForm: () => {
      isVisible = false;
      instanceUid = null;
      resetFloatingFormBaseline();
      notify();
    },
    registerSaveDraftHandler: (handler: SaveDraftHandler) => {
      saveDraftHandler = handler;
    },
    unregisterSaveDraftHandler: () => {
      saveDraftHandler = null;
    },
    registerStudyContextReset: (handler: (activeStudyUid: string) => void) => {
      studyContextResetHandler = handler;
    },
    unregisterStudyContextReset: () => {
      studyContextResetHandler = null;
    },
    notifyStudyContextChanged: (activeStudyUid: string) => {
      studyContextResetHandler?.(activeStudyUid);
    },
    triggerNavigateToStudiesList: () => {
      shouldNavigateToStudiesList = true;
      notifyNavigation();
    },
    shouldNavigateToStudiesList: () => {
      const result = shouldNavigateToStudiesList;
      shouldNavigateToStudiesList = false; // Reset after reading
      return result;
    },
    promptNavigation: async (onProceed: () => void | Promise<void>) => {
      const { isLoggedInUserStudyGrader } = await import(
        '../../../platform/app/src/utils/graderStudyViewOnly'
      );

      if (!(await isLoggedInUserStudyGrader())) {
        await logActivityAndProceed(onProceed);
        return;
      }

      const { hasUnsavedNewAnnotations } = await import(
        '../../../extensions/cornerstone/src/utils/annotationSaveUtils'
      );

      const hasCanvasUnsaved = await hasUnsavedNewAnnotations();
      const hasFormUnsaved = hasUnsavedFormFieldChanges();

      if (!hasCanvasUnsaved && !hasFormUnsaved) {
        await logActivityAndProceed(onProceed);
        return;
      }

      await new Promise<void>(resolve => {
        pendingNavigation = { onProceed, resolve };
        showUnsavedNavigationModal = true;
        notifyUnsavedNavigation();
      });
    },
    saveDraftAnnotations: saveDraftAnnotationsImpl,
    confirmUnsavedNavigationSave: async () => {
      if (!pendingNavigation || unsavedNavigationSaving) {
        return;
      }

      const { isLoggedInUserStudyGrader } = await import(
        '../../../platform/app/src/utils/graderStudyViewOnly'
      );

      if (!(await isLoggedInUserStudyGrader())) {
        await completePendingNavigation();
        return;
      }

      unsavedNavigationSaving = true;
      notifyUnsavedNavigation();

      const saved = await saveDraftAnnotationsImpl();

      if (saved) {
        await completePendingNavigation();
      } else {
        unsavedNavigationSaving = false;
        notifyUnsavedNavigation();
      }
    },
    discardUnsavedNavigationAndProceed: async () => {
      if (!pendingNavigation) {
        return;
      }
      await completePendingNavigation();
    },
    cancelUnsavedNavigation: () => {
      cancelPendingNavigation();
    },
    subscribe,
    subscribeToNavigation,
    subscribeToUnsavedNavigation,
  };
};

const floatingFormStore = createFloatingFormStore();

if (typeof window !== 'undefined') {
  (window as any).floatingFormStore = floatingFormStore;
}

const confirmationModalStyles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    zIndex: 10001,
    padding: '40px 20px 20px',
  },
  content: {
    backgroundColor: '#090C2A',
    border: '1px solid #3a3f99',
    borderRadius: '10px',
    width: '420px',
    maxWidth: '90vw',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column' as const,
    color: '#ffffff',
    boxShadow: '0 10px 25px rgba(0,0,0,0.4)',
    animation: 'fadeIn 0.2s ease-out',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid #3a3f99',
    flexShrink: 0,
  },
  body: {
    padding: '20px',
    fontSize: '14px',
    lineHeight: 1.6,
    color: '#d1d5db',
    overflowY: 'auto' as const,
    flex: '1 1 auto',
    minHeight: 0,
  },
  footer: {
    padding: '16px 20px',
    borderTop: '1px solid #3a3f99',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    flexShrink: 0,
  },
  buttonSecondary: {
    padding: '6px 14px',
    backgroundColor: 'transparent',
    color: '#ffffff',
    border: '1px solid #3a3f99',
    borderRadius: '7px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  buttonPrimary: {
    padding: '6px 14px',
    backgroundColor: '#1454d4',
    color: '#ffffff',
    border: 'none',
    borderRadius: '7px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: '#ffffff',
    fontSize: '20px',
    cursor: 'pointer',
    padding: 0,
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
};

// Hook to use the store
const useFloatingFormStore = () => {
  const [state, setState] = useState(floatingFormStore.getState());

  useEffect(() => {
    return floatingFormStore.subscribe(() => {
      setState(floatingFormStore.getState());
    });
  }, []);

  return {
    isVisible: state.isVisible,
    instanceUid: state.instanceUid,
    showForm: floatingFormStore.showForm,
    hideForm: floatingFormStore.hideForm,
  };
};

// Helper function to capture Cornerstone annotations
// When graderId is provided, only captures annotations belonging to that grader
// This prevents saving annotations from other graders when viewing merged annotations (admin/manager view)
const captureCornerstoneAnnotations = async (graderId?: number | string | null) => {
  try {
    // Import Cornerstone Tools dynamically
    const cornerstoneTools = window.cornerstoneTools || (await import('@cornerstonejs/tools')).default;

    if (!cornerstoneTools) {
      return null;
    }

    // Get all annotations from the annotation manager
    const allAnnotations = cornerstoneTools.annotation.state.getAllAnnotations();

    if (!allAnnotations || allAnnotations.length === 0) {
      return null;
    }


    // First, actually remove deleted annotations from Cornerstone state if they still exist
    // This ensures they're completely gone before we capture
    const annotationsToRemove: string[] = [];
    allAnnotations.forEach(ann => {
      if (deletedAnnotationUIDs.has(ann.annotationUID)) {
        annotationsToRemove.push(ann.annotationUID);
      }
    });

    if (annotationsToRemove.length > 0) {
      annotationsToRemove.forEach(uid => {
        try {
          cornerstoneTools.annotation.state.removeAnnotation(uid);
        } catch (error) {
          // Error removing annotation
        }
      });
    }

    // Get annotations again after removal to ensure we have the latest state
    const currentAnnotations = cornerstoneTools.annotation.state.getAllAnnotations();

    if (!currentAnnotations || currentAnnotations.length === 0) {
      return null;
    }

    // Normalize graderId to string for comparison
    const normalizedGraderId = graderId != null ? String(graderId) : null;

    // Filter out deleted annotations, invalidated annotations, ReferenceLines, and annotations from other graders
    const nonDeletedAnnotations = currentAnnotations.filter(ann => {
      if (deletedAnnotationUIDs.has(ann.annotationUID)) {
        return false;
      }
      // Exclude ReferenceLines - these are helper/guide lines that don't need to be saved
      if (ann.metadata?.toolName === 'ReferenceLines') {
        return false;
      }

      // If graderId is provided, filter annotations by graderId
      // This is critical when admin/manager is viewing merged annotations from multiple graders
      // We only want to save annotations that belong to the current user
      if (normalizedGraderId != null) {
        const annGraderId = ann.metadata?.graderId;

        // If annotation has no graderId, it's a newly created annotation (belongs to current user)
        // Include it in the save
        if (!annGraderId) {
          return true;
        }

        // Normalize annotation's graderId for comparison
        const normalizedAnnGraderId = String(annGraderId);

        // Only include annotations that match the current user's graderId
        if (normalizedAnnGraderId === normalizedGraderId) {
          return true;
        } else {
          // Exclude annotations from other graders
          console.log(`[captureCornerstoneAnnotations] Excluding annotation ${ann.annotationUID} (graderId: ${normalizedAnnGraderId}, expected: ${normalizedGraderId})`);
          return false;
        }
      }

      // If no graderId provided, include all annotations (backward compatibility)
      return true;
    });


    // Deduplicate annotations by annotationUID to prevent saving duplicates
    const seenUIDs = new Set<string>();
    const uniqueAnnotations = nonDeletedAnnotations.filter(ann => {
      if (seenUIDs.has(ann.annotationUID)) {
        return false;
      }
      seenUIDs.add(ann.annotationUID);
      return true;
    });


    // If no annotations remain after filtering, return null (empty state)
    if (uniqueAnnotations.length === 0) {
      console.log(`[captureCornerstoneAnnotations] No annotations to save after filtering (graderId: ${normalizedGraderId})`);
      return null;
    }

    console.log(`[captureCornerstoneAnnotations] Capturing ${uniqueAnnotations.length} annotations for graderId: ${normalizedGraderId}`);

    // Serialize annotations for storage
    const serializedAnnotations = uniqueAnnotations.map(ann => ({
      annotationUID: ann.annotationUID,
      highlighted: ann.highlighted,
      isLocked: ann.isLocked,
      metadata: {
        toolName: ann.metadata.toolName,
        referencedImageId: ann.metadata.referencedImageId,
        FrameOfReferenceUID: ann.metadata.FrameOfReferenceUID,
        viewPlaneNormal: ann.metadata.viewPlaneNormal,
        viewUp: ann.metadata.viewUp,
        // Preserve graderId and graderColor if they exist (for newly created annotations, these may not be set yet)
        graderId: ann.metadata?.graderId,
        graderColor: ann.metadata?.graderColor,
      },
      data: {
        ...ann.data, // Spread all data properties to preserve complete structure including contour
      }
    }));


    return {
      annotations: serializedAnnotations,
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    console.error('[captureCornerstoneAnnotations] Error capturing annotations:', error);
    return null;
  }
};

// Track deleted annotation UIDs to prevent them from being restored
// This is global so it persists even when the form component unmounts
const deletedAnnotationUIDs = new Set<string>();

// Track saved annotations that were resized/moved on canvas but not yet persisted
const modifiedAnnotationUIDs = new Set<string>();

// When we clear annotations temporarily for UI filtering (e.g. toggling grader visibility),
// Cornerstone fires ANNOTATION_REMOVED events. We don't want those temporary removals
// to mark annotations as deleted permanently (which would prevent drafts from restoring).
let suppressAnnotationRemovalTracking = false;

// Suppress ANNOTATION_MODIFIED tracking during programmatic restore/clear (same windows as above).
let suppressAnnotationModificationTracking = false;

// Master copy of all merged annotations (untouched, used for filtering)
// This stores the complete merged annotations array before any filtering
let masterMergedAnnotations: any[] | null = null;

// Track which graders are visible (graderId -> boolean)
// All graders are visible by default
const visibleGraders = new Map<string, boolean>();

// Store graderId -> color mapping (set during annotation merge, used for new annotations)
const graderIdToColorMap = new Map<string, string>();

// Track the study currently represented by restored Cornerstone annotations.
let lastRestoredStudyUid: string | null = null;

// Store current user's graderId globally (set when study loads)
let currentUserGraderId: number | string | null = null;

// Expose on window for access from other modules (e.g., removeMeasurement command)
if (typeof window !== 'undefined') {
  (window as any).deletedAnnotationUIDs = deletedAnnotationUIDs;
  (window as any).modifiedAnnotationUIDs = modifiedAnnotationUIDs;
  (window as any).masterMergedAnnotations = masterMergedAnnotations;
  (window as any).visibleGraders = visibleGraders;
  (window as any).graderIdToColorMap = graderIdToColorMap;
  (window as any).currentUserGraderId = currentUserGraderId;
}

// Global flag to track if the deletion listener is set up
let deletionListenerSetup = false;
// Global flag to track if the annotation creation listener is set up
let annotationCreationListenerSetup = false;
// Global flag to track if the annotation modification listener is set up
let modificationListenerSetup = false;

// Helper function to get color for a graderId
// First checks the global graderIdToColorMap (set during merge), then falls back to palette
const getColorForGraderId = (graderId: number | string | null | undefined): string => {
  // Use the same color palette as in restoreCornerstoneAnnotationsFromData
  const colorPalette: string[] = [
    '#00E5FF', // Cyan
    '#FFD60A', // Yellow
    '#B5179E', // Purple
    '#3A86FF', // Blue
    '#FF8C00', // Orange
    '#00FF88', // Green
    '#FFFFFF', // White (fallback)
  ];

  if (graderId == null) {
    return colorPalette[0]; // Default to first color
  }

  const graderIdStr = String(graderId);

  // First, check if we have a color mapped for this graderId (from merge)
  if (graderIdToColorMap.has(graderIdStr)) {
    return graderIdToColorMap.get(graderIdStr)!;
  }

  // If not in map, use a consistent hash-based assignment
  // This ensures the same graderId always gets the same color
  const hash = graderIdStr.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const color = colorPalette[hash % colorPalette.length];

  // Store it in the map for future use
  graderIdToColorMap.set(graderIdStr, color);

  return color;
};

// Setup global annotation creation listener to assign grader colors to new annotations
const setupGlobalAnnotationCreationListener = async () => {
  if (annotationCreationListenerSetup) {
    return; // Already set up
  }

  try {
    const cornerstoneTools = window.cornerstoneTools || (await import('@cornerstonejs/tools')).default;
    if (!cornerstoneTools) {
      return;
    }

    // Dispatch site is @cornerstonejs/core eventTarget (same as tools init).
    const { Enums } = await import('@cornerstonejs/tools');
    const csToolsEvents = Enums.Events;

    const handleAnnotationCreated = async (event: any) => {
      try {
        console.log('[setupGlobalAnnotationCreationListener] Annotation creation event received:', event.type);
        const annotation = event.detail?.annotation;
        if (!annotation || !annotation.annotationUID) {
          console.log('[setupGlobalAnnotationCreationListener] No annotation or annotationUID in event');
          return;
        }

        console.log('[setupGlobalAnnotationCreationListener] Processing annotation:', {
          annotationUID: annotation.annotationUID,
          toolName: annotation.metadata?.toolName,
          hasGraderId: !!annotation.metadata?.graderId,
          graderId: annotation.metadata?.graderId
        });

        // Skip ReferenceLines annotations
        if (annotation.metadata?.toolName === 'ReferenceLines') {
          console.log('[setupGlobalAnnotationCreationListener] Skipping ReferenceLines annotation');
          return;
        }

        // Check if annotation already has graderId (it's a restored annotation, not a new one)
        if (annotation.metadata?.graderId) {
          console.log('[setupGlobalAnnotationCreationListener] Annotation already has graderId, skipping:', annotation.metadata.graderId);
          return; // Already has graderId, skip
        }

        // Get current user's graderId from multiple sources
        let currentGraderId: number | string | null = null;

        // First, try global variable
        currentGraderId = (window as any).currentUserGraderId || currentUserGraderId;
        console.log('[setupGlobalAnnotationCreationListener] Current graderId from global:', currentGraderId);

        // If not found, try annotationsData
        if (!currentGraderId) {
          try {
            const annotationsData = (window as any).annotationsData;
            if (annotationsData?.data?.[0]?.study?.grader_id) {
              currentGraderId = annotationsData.data[0].study.grader_id;
              console.log('[setupGlobalAnnotationCreationListener] Found graderId from annotationsData:', currentGraderId);
            }
          } catch (error) {
            console.warn('[setupGlobalAnnotationCreationListener] Error getting graderId from annotationsData:', error);
          }
        }

        // If still not found, try study context
        if (!currentGraderId) {
          try {
            const studyContext = (window as any).currentStudyContext;
            if (studyContext?.grader_id) {
              currentGraderId = studyContext.grader_id;
              console.log('[setupGlobalAnnotationCreationListener] Found graderId from studyContext:', currentGraderId);
            }
          } catch (error) {
            console.warn('[setupGlobalAnnotationCreationListener] Error getting graderId from studyContext:', error);
          }
        }

        // Always assign color if we have a graderId (not just for admin/manager)
        // This ensures all new annotations get the correct color
        if (currentGraderId != null) {
          const normalizedGraderId = String(currentGraderId);
          const graderColor = getColorForGraderId(currentGraderId);

          // Get the annotation from Cornerstone state (not just from event)
          // This ensures we're modifying the actual annotation in state
          let annotationInState = annotation;
          try {
            const annotationFromState = cornerstoneTools.annotation.state.getAnnotation(annotation.annotationUID);
            if (annotationFromState) {
              annotationInState = annotationFromState;
            }
          } catch (error) {
            // If we can't get from state, use the one from event
            console.warn('[setupGlobalAnnotationCreationListener] Could not get annotation from state, using event annotation');
          }

          // Update annotation metadata with graderId and graderColor
          if (!annotationInState.metadata) {
            annotationInState.metadata = {};
          }
          annotationInState.metadata.graderId = normalizedGraderId;
          annotationInState.metadata.graderColor = graderColor;

          // Apply grader stroke color only when no palette color is set.
          // The palette listener is synchronous; this handler is async. Re-read state
          // after await so we do not overwrite palette styles with a stale hasPaletteColor.
          try {
            const toolsModule = await import('@cornerstonejs/tools');
            const csAnnotation = toolsModule.annotation;

            let freshAnnotation = annotationInState;
            try {
              const fromState = cornerstoneTools.annotation.state.getAnnotation(
                annotation.annotationUID
              );
              if (fromState) {
                freshAnnotation = fromState;
              }
            } catch {
              // use annotationInState
            }

            const hasPaletteColor =
              !!freshAnnotation.data?.annotationColor ||
              !!freshAnnotation.metadata?.annotationColor;
            if (hasPaletteColor) {
              return;
            }

            if (!freshAnnotation.data) {
              freshAnnotation.data = {};
            }
            freshAnnotation.data.color = graderColor;

            // Convert hex to rgb if needed
            let colorString = graderColor;
            if (colorString.startsWith('#')) {
              const hex = colorString.slice(1);
              if (hex.length === 6) {
                const r = parseInt(hex.slice(0, 2), 16);
                const g = parseInt(hex.slice(2, 4), 16);
                const b = parseInt(hex.slice(4, 6), 16);
                colorString = `rgb(${r}, ${g}, ${b})`;
              }
            }

            // Apply the color style to the annotation
            csAnnotation.config.style.setAnnotationStyles(annotation.annotationUID, {
              color: colorString,
              colorSelected: colorString,
              colorHighlighted: colorString,
              textBoxColor: colorString,
              textBoxColorSelected: colorString,
              textBoxColorHighlighted: colorString,
              lineWidth: 2.5,
              lineWidthSelected: 2.5,
            });

            // Force a render to update the display
            try {
              const { getRenderingEngines } = await import('@cornerstonejs/core');
              const renderingEngines = getRenderingEngines();
              if (renderingEngines && renderingEngines.length > 0) {
                renderingEngines.forEach((engine: any) => {
                  const viewports = engine.getViewports();
                  viewports.forEach((viewport: any) => {
                    viewport.render();
                  });
                });
              }
            } catch (renderError) {
              // Could not trigger render
            }

            console.log(`[setupGlobalAnnotationCreationListener] Successfully assigned color "${graderColor}" (${colorString}) to new annotation ${annotation.annotationUID} for grader "${normalizedGraderId}"`);
          } catch (error) {
            console.error('[setupGlobalAnnotationCreationListener] Failed to apply color to new annotation:', annotation.annotationUID, error);
          }
        } else {
          console.warn('[setupGlobalAnnotationCreationListener] No graderId found, cannot assign color to annotation:', annotation.annotationUID);
        }
      } catch (error) {
        console.error('[setupGlobalAnnotationCreationListener] Error handling annotation creation:', error);
      }
    };

    if (!eventTarget || typeof eventTarget.addEventListener !== 'function') {
      return;
    }

    // Listen for both ANNOTATION_ADDED and ANNOTATION_COMPLETED events
    eventTarget.addEventListener(csToolsEvents.ANNOTATION_ADDED, handleAnnotationCreated);
    eventTarget.addEventListener(csToolsEvents.ANNOTATION_COMPLETED, handleAnnotationCreated);
    annotationCreationListenerSetup = true;
  } catch (error) {
    console.warn('[setupGlobalAnnotationCreationListener] Error setting up annotation creation listener:', error);
  }
};

// Setup global annotation modification listener (tracks resize/move of saved annotations)
const setupGlobalAnnotationModificationListener = async () => {
  if (modificationListenerSetup) {
    return;
  }

  try {
    const cornerstoneTools = window.cornerstoneTools || (await import('@cornerstonejs/tools')).default;
    if (!cornerstoneTools) {
      return;
    }

    const { Enums } = await import('@cornerstonejs/tools');
    const csToolsEvents = Enums.Events;

    const handleAnnotationModified = async (event: any) => {
      if (suppressAnnotationModificationTracking) {
        return;
      }

      try {
        const annotation = event.detail?.annotation;
        const annotationUID = annotation?.annotationUID;
        if (!annotationUID) {
          return;
        }

        const toolName = annotation.metadata?.toolName;
        if (toolName === 'ReferenceLines' || toolName === 'Selection') {
          return;
        }

        if (deletedAnnotationUIDs.has(annotationUID)) {
          return;
        }

        const { isAnnotationSaved } = await import(
          '../../../extensions/cornerstone/src/utils/annotationSaveUtils'
        );

        const wasRestoredFromApi = annotation.metadata?.__restoredFromApi === true;

        let effectiveGraderId: string | null =
          (window as any).currentUserGraderId != null
            ? String((window as any).currentUserGraderId)
            : currentUserGraderId != null
              ? String(currentUserGraderId)
              : null;

        if (!effectiveGraderId) {
          try {
            const annotationsData = (window as any).annotationsData;
            if (annotationsData?.data?.[0]?.study?.grader_id) {
              effectiveGraderId = String(annotationsData.data[0].study.grader_id);
            }
          } catch {
            // ignore
          }
        }

        const annGraderId = annotation.metadata?.graderId;
        if (effectiveGraderId != null && annGraderId != null) {
          if (String(annGraderId) !== effectiveGraderId) {
            return;
          }
        }

        if (!isAnnotationSaved(annotationUID) && !wasRestoredFromApi) {
          // New annotations are already detected via UID absence in the saved snapshot.
          return;
        }

        modifiedAnnotationUIDs.add(annotationUID);
      } catch (error) {
        console.warn('[setupGlobalAnnotationModificationListener] Error handling modification:', error);
      }
    };

    if (!eventTarget || typeof eventTarget.addEventListener !== 'function') {
      return;
    }

    eventTarget.addEventListener(csToolsEvents.ANNOTATION_MODIFIED, handleAnnotationModified);
    modificationListenerSetup = true;
  } catch (error) {
    console.warn('[setupGlobalAnnotationModificationListener] Error setting up listener:', error);
  }
};

// Setup global annotation deletion listener (runs once, persists across component mounts)
const setupGlobalAnnotationDeletionListener = async () => {
  if (deletionListenerSetup) {
    return; // Already set up
  }

  try {
    const cornerstoneTools = window.cornerstoneTools || (await import('@cornerstonejs/tools')).default;
    if (!cornerstoneTools) {
      return;
    }

    // Events (including ANNOTATION_REMOVED / ANNOTATION_COMPLETED) are dispatched on
    // @cornerstonejs/core eventTarget — not on the tools namespace object.
    const { Enums } = await import('@cornerstonejs/tools');
    const csToolsEvents = Enums.Events;

    const handleAnnotationRemoved = async (event: any) => {
      const annotationUID =
        event.detail?.annotation?.annotationUID ?? event.detail?.annotationUID;
      if (annotationUID) {
        if (!suppressAnnotationRemovalTracking) {
          deletedAnnotationUIDs.add(annotationUID);
          modifiedAnnotationUIDs.delete(annotationUID);
        }


        // Ensure the annotation is actually removed from Cornerstone state
        // Sometimes the state might not be fully updated, so we force removal
        try {
          // Get fresh reference to cornerstone tools in case it changed
          const currentCornerstoneTools = window.cornerstoneTools || cornerstoneTools;
          if (currentCornerstoneTools) {
            const existingAnnotation = currentCornerstoneTools.annotation.state.getAnnotation(annotationUID);
            if (existingAnnotation) {
              currentCornerstoneTools.annotation.state.removeAnnotation(annotationUID);
            }
          }
        } catch (error) {
          // Annotation might already be removed, which is fine
        }

        // Also update window.annotationsData to remove the deleted annotation
       // (Do not update when we are suppressing removal tracking for temporary clears.)
       if (!suppressAnnotationRemovalTracking && window.annotationsData?.data?.[0]?.annotations?.[0]?.data) {
          const dataObj = window.annotationsData.data[0].annotations[0].data;
          let annotationsData = null;

          if (dataObj?.rawdata) {
            annotationsData = dataObj.rawdata;
          } else if (dataObj?.annotations) {
            let parsed = typeof dataObj.annotations === 'string' ? JSON.parse(dataObj.annotations) : dataObj.annotations;
            if (parsed.annotations && typeof parsed.annotations === 'object' && parsed.annotations.annotations) {
              parsed = parsed.annotations;
            }
            annotationsData = parsed;
          }

          if (annotationsData?.annotations) {
            // Remove the deleted annotation from the annotations array
            const beforeCount = annotationsData.annotations.length;
            const beforeUIDs = annotationsData.annotations.map((ann: any) => ann.annotationUID);
            annotationsData.annotations = annotationsData.annotations.filter(
              (ann: any) => ann.annotationUID !== annotationUID
            );
            const afterCount = annotationsData.annotations.length;
            const afterUIDs = annotationsData.annotations.map((ann: any) => ann.annotationUID);

            // Update the data structure
            if (dataObj?.rawdata) {
              dataObj.rawdata = annotationsData;
            } else if (dataObj?.annotations) {
              dataObj.annotations = typeof dataObj.annotations === 'string'
                ? JSON.stringify(annotationsData)
                : annotationsData;
            }

            // Verify the update
            const verifyDataObj = window.annotationsData?.data?.[0]?.annotations?.[0]?.data;
            if (verifyDataObj?.rawdata) {
              const verifyCount = verifyDataObj.rawdata?.annotations?.length || 0;
            }
          }
        }
      }
    };

    if (!eventTarget || typeof eventTarget.addEventListener !== 'function') {
      return;
    }

    eventTarget.addEventListener(csToolsEvents.ANNOTATION_REMOVED, handleAnnotationRemoved);
    deletionListenerSetup = true;
  } catch (error) {
    // Error setting up global annotation deletion listener
  }
};

// Initialize the global listeners when the module loads
if (typeof window !== 'undefined') {
  // Set up listeners asynchronously when cornerstone tools are available
  const initListener = async () => {
    // Wait a bit for cornerstone tools to be initialized
    let attempts = 0;
    const maxAttempts = 10;
    while (attempts < maxAttempts) {
      if (window.cornerstoneTools) {
        await setupGlobalAnnotationDeletionListener();
        await setupGlobalAnnotationCreationListener();
        await setupGlobalAnnotationModificationListener();
        break;
      }
      await new Promise(resolve => setTimeout(resolve, 500));
      attempts++;
    }
  };
  initListener();
}

// Helper function to clear all annotations from Cornerstone Tools state
// preserveMaster: if true, don't clear masterMergedAnnotations and visibleGraders (used for filtering)
const clearAllCornerstoneAnnotations = async (preserveMaster: boolean = false) => {
  try {
    const cornerstoneTools = window.cornerstoneTools || (await import('@cornerstonejs/tools')).default;
    if (cornerstoneTools && cornerstoneTools.annotation && cornerstoneTools.annotation.state) {
      const allAnnotations = cornerstoneTools.annotation.state.getAllAnnotations();
      if (allAnnotations && allAnnotations.length > 0) {
        // Remove all annotations one by one to ensure proper cleanup
        allAnnotations.forEach((ann: any) => {
          try {
            cornerstoneTools.annotation.state.removeAnnotation(ann.annotationUID);
          } catch (error) {
            // Error removing annotation
          }
        });

        // Also try removeAllAnnotations if available (more efficient)
        if (typeof cornerstoneTools.annotation.state.removeAllAnnotations === 'function') {
          cornerstoneTools.annotation.state.removeAllAnnotations();
        }

        // Clear master annotations and visibility state when clearing all annotations
        // This happens when switching studies or when API returns no annotations
        // Skip this if preserveMaster is true (used during filtering)
        if (!preserveMaster) {
          masterMergedAnnotations = null;
          visibleGraders.clear();
          graderIdToColorMap.clear(); // Clear color map when switching studies
        }

        // Trigger a render to update the viewport
        try {
          const { getRenderingEngines } = await import('@cornerstonejs/core');
          const renderingEngines = getRenderingEngines();
          if (renderingEngines && renderingEngines.length > 0) {
            renderingEngines.forEach((engine: any) => {
              const viewports = engine.getViewports();
              viewports.forEach((viewport: any) => {
                viewport.render();
              });
            });
          }
        } catch (renderError) {
          // Could not trigger render after clearing annotations
        }
      }
    }
  } catch (clearError) {
    // Error clearing annotations from memory
  }
};

// Helper function to ensure a valid spline instance for SplineROI annotations
const ensureValidSplineInstanceForSplineROI = (ann: any, cornerstoneTools: any) => {
  try {
    if (!ann?.data) {
      return false;
    }

    // Prefer existing spline block if present; otherwise create one and
    // derive control points from handles.points as a fallback so that
    // annotations saved without data.spline can still be restored.
    const splineData = ann.data.spline || {};
    const existingInstance = splineData.instance;

    // If there is already a valid spline instance with the required methods, keep it.
    if (
      existingInstance &&
      typeof existingInstance.setControlPoints === 'function' &&
      typeof existingInstance.isPointNearCurve === 'function'
    ) {
      return true;
    }

    // Derive control points:
    // 1) from saved spline instance or splineData if available
    // 2) otherwise from handles.points (most common in older saves)
    const handlesPoints =
      ann.data.handles?.points && Array.isArray(ann.data.handles.points)
        ? ann.data.handles.points.map((p: any) => [p[0], p[1]])
        : null;

    const controlPoints =
      existingInstance?._controlPoints ||
      splineData.instance?._controlPoints ||
      splineData._controlPoints ||
      handlesPoints;

    if (!controlPoints || !Array.isArray(controlPoints) || controlPoints.length === 0) {
      // No control points to reconstruct from
      return false;
    }

    // Get SplineROITool configuration to determine which spline class to use
    const SplineROITool = cornerstoneTools?.SplineROITool;
    if (!SplineROITool) {
      return false;
    }

    const splineToolInstance = new SplineROITool();
    const splineConfig = splineToolInstance.configuration?.spline;

    if (!splineConfig || !splineConfig.configuration) {
      return false;
    }

    const splineTypeFromData = splineData.type;
    const splineType = splineTypeFromData || splineConfig.type;

    const splineTypeConfig = splineConfig.configuration[splineType];
    const SplineClass = splineTypeConfig?.Class;

    if (!SplineClass) {
      return false;
    }

    // Create a new spline instance and populate it from saved data
    const splineInstance = new SplineClass();

    // Restore basic properties from saved instance if available
    const savedInstance = existingInstance || splineData.instance || {};

    if (typeof savedInstance._resolution === 'number') {
      try {
        splineInstance.resolution = savedInstance._resolution;
      } catch {
        // Ignore resolution restore errors
      }
    }

    if (typeof savedInstance._closed === 'boolean') {
      try {
        splineInstance.closed = savedInstance._closed;
      } catch {
        // Ignore closed restore errors
      }
    }

    // Set control points (this will compute curve segments lazily when needed)
    splineInstance.setControlPoints(controlPoints);

    // Attach reconstructed instance (and cached control points) back to annotation data
    ann.data.spline = {
      ...splineData,
      type: splineType,
      _controlPoints: controlPoints,
      instance: splineInstance,
    };

    return true;
  } catch {
    // Any failure means we could not reconstruct a valid spline
    return false;
  }
};

const COMPARE_MARKER_TOOLS = [
  'Length',
  'Bidirectional',
  'ArrowAnnotate',
  'EllipticalROI',
  'RectangleROI',
  'CircleROI',
  'PlanarFreehandROI',
  'SplineROI',
  'LivewireContour',
  'Angle',
  'CobbAngle',
  'Probe',
];

const getLegendMarkerToken = (color?: string): string => {
  const normalized = (color || '').toLowerCase();

  if (normalized.includes('ffd60a') || normalized.includes('ffff00')) {
    return '🟡';
  }
  if (normalized.includes('b5179e') || normalized.includes('ff00ff') || normalized.includes('purple')) {
    return '🟣';
  }
  if (normalized.includes('ff8c00') || normalized.includes('ff7f27') || normalized.includes('orange')) {
    return '🟠';
  }
  if (normalized.includes('00ff88') || normalized.includes('22b14c') || normalized.includes('00ff00')) {
    return '🟢';
  }
  if (normalized.includes('ffffff') || normalized.includes('white')) {
    return '⚪';
  }
  return '🔵';
};

const applyCompareMarkerTextLinesToToolGroups = async () => {
  try {
    const toolsModule = await import('@cornerstonejs/tools');
    const { ToolGroupManager } = toolsModule;
    const allGroups = ToolGroupManager.getAllToolGroups?.() || [];

    allGroups.forEach((toolGroup: any) => {
      COMPARE_MARKER_TOOLS.forEach((toolName: string) => {
        try {
          const existingConfig = toolGroup?.getToolConfiguration?.(toolName) || {};
          const toolInstanceGetTextLines =
            toolGroup?.getToolInstance?.(toolName)?.configuration?.getTextLines;
          const existingGetTextLines =
            existingConfig?.getTextLines || toolInstanceGetTextLines;

          if (typeof existingGetTextLines !== 'function') {
            return;
          }

          // Wrap only once.
          if ((existingGetTextLines as any).__compareMarkerWrapped) {
            return;
          }

          const wrappedGetTextLines = (data: any, targetId: any, ...rest: any[]) => {
            const lines = existingGetTextLines(data, targetId, ...rest);
            if (!Array.isArray(lines) || !lines.length) {
              return lines;
            }

            const marker = data?.__graderMarker;
            if (!marker) {
              return lines;
            }

            // Keep marker only on first line and indent continuation lines so
            // measurement values visually align in a single text column.
            // SVG text rendering can collapse/trim normal spaces; use NBSP for visible indent.
            const continuationIndent = '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0';
            return [
              `${marker} ${lines[0]}`,
              ...lines.slice(1).map(line => `${continuationIndent}${line}`),
            ];
          };

          (wrappedGetTextLines as any).__compareMarkerWrapped = true;
          toolGroup.setToolConfiguration(
            toolName,
            {
              ...existingConfig,
              getTextLines: wrappedGetTextLines,
            },
            true
          );
        } catch {
          // Keep restoring flow resilient.
        }
      });
    });
  } catch {
    // Keep restoring flow resilient.
  }
};

// Helper function to restore Cornerstone annotations
const restoreCornerstoneAnnotations = async (annotationData: any) => {
  const previousSuppressRemovalTracking = suppressAnnotationRemovalTracking;
  const previousSuppressModificationTracking = suppressAnnotationModificationTracking;

  suppressAnnotationRemovalTracking = true;
  suppressAnnotationModificationTracking = true;
  try {

    if (!annotationData) {
      return;
    }

    if (!annotationData.annotations) {
      return;
    }

    if (annotationData.annotations.length === 0) {
      return;
    }


    // Import Cornerstone Tools dynamically
    const cornerstoneTools = window.cornerstoneTools || (await import('@cornerstonejs/tools')).default;

    if (!cornerstoneTools) {
      return;
    }


    // Remove only invalid SplineROI annotations from state (ones with invalid spline.instance objects)
    // Valid SplineROI annotations (newly created) should be kept as they have proper spline.instance objects with methods
    try {
      const allAnnotations = cornerstoneTools.annotation.state.getAllAnnotations();
      if (allAnnotations && allAnnotations.length > 0) {
        allAnnotations.forEach((ann: any) => {
          if (ann.metadata?.toolName === 'SplineROI') {
            // Only remove if the spline.instance is invalid (doesn't have required methods)
            // This happens when annotations are restored from JSON (methods are lost)
            const hasInvalidSplineInstance = ann.data?.spline?.instance &&
              typeof ann.data.spline.instance.setControlPoints !== 'function';
            if (hasInvalidSplineInstance) {
              try {
                cornerstoneTools.annotation.state.removeAnnotation(ann.annotationUID);
              } catch (error) {
                // Error removing invalid SplineROI annotation - ignore
              }
            }
          }
        });
      }
    } catch (error) {
      // Error checking/removing invalid SplineROI annotations - ignore
    }

    // Get existing annotations to check for duplicates
    const existingAnnotations = cornerstoneTools.annotation.state.getAllAnnotations();
    const existingUIDs = new Set(existingAnnotations.map(ann => ann.annotationUID));

    // Build a map of active imageIds in all rendering viewports to help resolve dynamic query params or protocol differences in referencedImageId
    const activeImageIdMap = new Map<string, string>(); // Map from SOPInstanceUID_frameNumber to the active imageId string
    try {
      const { getRenderingEngines } = await import('@cornerstonejs/core');
      const renderingEngines = getRenderingEngines();
      if (renderingEngines && renderingEngines.length > 0) {
        renderingEngines.forEach((engine: any) => {
          const viewports = engine.getViewports();
          viewports.forEach((viewport: any) => {
            if (typeof viewport.getImageIds === 'function') {
              const imageIds = viewport.getImageIds() || [];
              imageIds.forEach((id: string) => {
                let sopUID = '';
                let frameNum = '1';

                // WADO-RS style
                const wadorsMatch = id.match(/instances\/([0-9.]+)(?:\/frames\/(\d+))?/i);
                if (wadorsMatch) {
                  sopUID = wadorsMatch[1];
                  frameNum = wadorsMatch[2] || '1';
                } else {
                  // WADO-URI/Query parameters style
                  const objectUIDMatch = id.match(/[&?]objectUID=([0-9.]+)/i);
                  const sopInstanceUIDMatch = id.match(/[&?]sopInstanceUID=([0-9.]+)/i);
                  sopUID = objectUIDMatch ? objectUIDMatch[1] : (sopInstanceUIDMatch ? sopInstanceUIDMatch[1] : '');

                  const frameMatch = id.match(/[&?]frameNumber=(\d+)/i);
                  frameNum = frameMatch ? frameMatch[1] : '1';
                }

                if (sopUID) {
                  const key = `${sopUID}_${frameNum}`;
                  activeImageIdMap.set(key, id);
                }
              });
            }
          });
        });
      }
    } catch (err) {
      console.warn('[restoreCornerstoneAnnotations] Failed to build active imageId map:', err);
    }

    // Determine if we should apply per-grader colors for these annotations.
    // This is only set to true when an admin/manager is viewing merged grader data.
    const useGraderColors = !!(annotationData as any).__useGraderColors;

    // If an annotation from saved data doesn't exist in current state AND is in deletedAnnotationUIDs,
    // it means it was deleted. Don't restore it.
    // Note: We don't automatically mark annotations as deleted just because they're missing from state
    // (could happen on study reload), but if they're in deletedAnnotationUIDs, they were explicitly deleted.

    // Restore each annotation, skipping duplicates and deleted annotations
    let restoredCount = 0;
    let skippedCount = 0;
    let deletedCount = 0;
    for (const ann of annotationData.annotations) {
      try {
        // Check if annotation was deleted by user - skip it
        if (deletedAnnotationUIDs.has(ann.annotationUID)) {
          deletedCount++;
          continue;
        }

        // Skip ReferenceLines annotations - these are helper/guide lines that shouldn't be restored
        if (ann.metadata?.toolName === 'ReferenceLines') {
          skippedCount++;
          continue;
        }

        // Check if annotation with this UID already exists
        if (existingUIDs.has(ann.annotationUID)) {
          skippedCount++;
          continue;
        }

        // Double-check: If annotation is in deletedAnnotationUIDs, don't restore it
        // This is a safety check in case it wasn't filtered out earlier
        if (deletedAnnotationUIDs.has(ann.annotationUID)) {
          deletedCount++;
          continue;
        }

        // Validate annotation structure based on tool type
        const toolName = ann.metadata?.toolName;

        // Handle SplineROI: reconstruct a valid spline instance from saved control points.
        if (toolName === 'SplineROI') {
          const reconstructed = ensureValidSplineInstanceForSplineROI(ann, cornerstoneTools);
          if (!reconstructed) {
            // If we cannot reconstruct a valid spline instance, skip this annotation
            skippedCount++;
            continue;
          }
        }

        // For PlanarFreehandROI and LivewireContour, ensure contour data exists
        if (toolName === 'PlanarFreehandROI' || toolName === 'LivewireContour') {
          if (!ann.data || !ann.data.contour) {
            skippedCount++;
            continue;
          }

          // For PlanarFreehandROI and LivewireContour, ensure polyline exists
          if (!ann.data.contour.polyline) {
            skippedCount++;
            continue;
          }
        }

        // Normalize referencedImageId to match the active viewport's imageId exactly
        let normalizedImageId = ann.metadata?.referencedImageId;
        if (normalizedImageId) {
          let savedSop = '';
          let savedFrame = '1';

          const wadorsMatch = normalizedImageId.match(/instances\/([0-9.]+)(?:\/frames\/(\d+))?/i);
          if (wadorsMatch) {
            savedSop = wadorsMatch[1];
            savedFrame = wadorsMatch[2] || '1';
          } else {
            const objectUIDMatch = normalizedImageId.match(/[&?]objectUID=([0-9.]+)/i);
            const sopInstanceUIDMatch = normalizedImageId.match(/[&?]sopInstanceUID=([0-9.]+)/i);
            savedSop = objectUIDMatch ? objectUIDMatch[1] : (sopInstanceUIDMatch ? sopInstanceUIDMatch[1] : '');

            const frameMatch = normalizedImageId.match(/[&?]frameNumber=(\d+)/i);
            savedFrame = frameMatch ? frameMatch[1] : '1';
          }

          if (savedSop) {
            const key = `${savedSop}_${savedFrame}`;
            if (activeImageIdMap.has(key)) {
              normalizedImageId = activeImageIdMap.get(key);
            }
          }
        }

        // Restore the complete annotation with all properties
        // The spread preserves metadata.graderColor and data.color from the merged annotation
        const isDraftAnnotation = ann.metadata?.__isDraft === true;
        const restoredAnnotation = {
          ...ann, // Spread all original properties (including metadata.graderColor and data.color from merge)
          metadata: {
            ...(ann.metadata || {}),
            ...(isDraftAnnotation ? {} : { __restoredFromApi: true }),
            referencedImageId: normalizedImageId,
          },
          highlighted: ann.highlighted ?? false,
          isLocked: ann.isLocked ?? false,
          invalidated: ann.invalidated ?? false,
        };

        if (useGraderColors) {
          // In merged/admin view, we color by grader.
          // Show the legend marker only for saved grader annotations.
          // Draft/unsaved annotations are tagged with `metadata.__isDraft` during toggle restore.
          restoredAnnotation.data = restoredAnnotation.data || {};

          const isDraft = restoredAnnotation?.metadata?.__isDraft === true;

          if (isDraft) {
            // Ensure draft markings don't show legend.
            if ((restoredAnnotation.data as any).__graderMarker) {
              delete (restoredAnnotation.data as any).__graderMarker;
            }
          } else {
            const graderLegendColor =
              restoredAnnotation?.metadata?.graderColor ||
              restoredAnnotation?.data?.color ||
              ann?.metadata?.graderColor ||
              ann?.data?.color;

            const markerToken = getLegendMarkerToken(graderLegendColor);
            restoredAnnotation.data.__graderMarker = markerToken;

            // Prefix marker into label text so it appears in the annotation label.
            const textValue =
              restoredAnnotation?.data?.text != null
                ? String(restoredAnnotation.data.text)
                : '';
            if (textValue && !textValue.startsWith(`${markerToken} `)) {
              restoredAnnotation.data.text = `${markerToken} ${textValue}`;
            }

            const labelValue =
              restoredAnnotation?.data?.label != null
                ? String(restoredAnnotation.data.label)
                : '';
            if (labelValue && !labelValue.startsWith(`${markerToken} `)) {
              restoredAnnotation.data.label = `${markerToken} ${labelValue}`;
            }
          }
        }

        // Add annotation back to Cornerstone state
        cornerstoneTools.annotation.state.addAnnotation(restoredAnnotation);

        // Apply per-annotation color from API (`data.annotationColor` / `metadata.annotationColor`)
        // whenever present. This must run for merged grader view too — previously we only applied it
        // when !useGraderColors, which forced every annotation to the per-grader palette color and
        // ignored saved per-annotation colors from the server.
        let didApplyPerAnnotationColor = false;
        const savedAnnotationColor =
          restoredAnnotation?.metadata?.annotationColor ?? restoredAnnotation?.data?.annotationColor;

        if (savedAnnotationColor) {
          try {
            const toolsModule = await import('@cornerstonejs/tools');
            const csAnnotation = toolsModule.annotation;

            // Cornerstone style expects an RGB string like 'rgb(255, 0, 0)'.
            // We may have stored hex (#RRGGBB) from the palette.
            let colorString = savedAnnotationColor as string;
            if (colorString.startsWith('#')) {
              const hex = colorString.slice(1);
              if (hex.length === 6) {
                const r = parseInt(hex.slice(0, 2), 16);
                const g = parseInt(hex.slice(2, 4), 16);
                const b = parseInt(hex.slice(4, 6), 16);
                colorString = `rgb(${r}, ${g}, ${b})`;
              }
            }

            csAnnotation.config.style.setAnnotationStyles(restoredAnnotation.annotationUID, {
              color: colorString,
              colorSelected: colorString,
              colorHighlighted: colorString,
              colorLocked: colorString,
              textBoxColor: colorString,
              textBoxColorSelected: colorString,
              textBoxColorHighlighted: colorString,
              textBoxColorLocked: colorString,
              lineWidth: 2.5,
              lineWidthSelected: 2.5,
            });

            didApplyPerAnnotationColor = true;
          } catch {
            // If anything fails, keep existing behavior (no hard failure).
          }
        }

        // Only apply per-grader colors when explicitly enabled (admin/manager merged view)
        // and the annotation has no saved per-annotation color from the API.
        if (useGraderColors && !didApplyPerAnnotationColor) {
          // Get grader color from metadata (set during merge) or from data.color as fallback
          // Check restoredAnnotation first (has merged metadata), then fall back to original ann
          const graderColor =
            restoredAnnotation?.metadata?.graderColor ||
            restoredAnnotation?.data?.color ||
            ann?.metadata?.graderColor ||
            ann?.data?.color;

          const graderId = restoredAnnotation?.metadata?.graderId || ann?.metadata?.graderId;
          const graderName = restoredAnnotation?.metadata?.graderName || restoredAnnotation?.metadata?.graderDisplayName || 'unknown';
          if (graderId && graderColor) {
            console.log(`[restoreCornerstoneAnnotations] Restoring annotation ${restoredAnnotation.annotationUID} for grader "${graderId}" (${graderName}) with color "${graderColor}"`);
          } else {
            console.warn(`[restoreCornerstoneAnnotations] Annotation ${restoredAnnotation.annotationUID} missing graderId or graderColor:`, {
              graderId,
              graderColor,
              metadata: restoredAnnotation?.metadata,
              dataColor: restoredAnnotation?.data?.color
            });
          }

          if (graderColor) {
            try {
              const toolsModule = await import('@cornerstonejs/tools');
              const csAnnotation = toolsModule.annotation;

              // Cornerstone style expects an RGB string like 'rgb(255, 0, 0)'.
              // If we already stored that format, use directly; otherwise accept hex.
              let colorString = graderColor as string;

              if (colorString.startsWith('#')) {
                // Convert simple hex (#RRGGBB) to rgb()
                const hex = colorString.slice(1);
                if (hex.length === 6) {
                  const r = parseInt(hex.slice(0, 2), 16);
                  const g = parseInt(hex.slice(2, 4), 16);
                  const b = parseInt(hex.slice(4, 6), 16);
                  colorString = `rgb(${r}, ${g}, ${b})`;
                }
              }

              // Apply the color style to the annotation
              csAnnotation.config.style.setAnnotationStyles(restoredAnnotation.annotationUID, {
                // Base stroke color
                color: colorString,
                // Use the same color when hovered/selected so it doesn't switch to global green
                colorSelected: colorString,
                colorHighlighted: colorString,
                // Lock so palette changes don't visually override restored grader colors.
                colorLocked: colorString,
                textBoxColor: colorString,
                textBoxColorSelected: colorString,
                textBoxColorHighlighted: colorString,
                textBoxColorLocked: colorString,
                // Slightly thicker lines to make colors appear brighter/clearer
                lineWidth: 2.5,
                lineWidthSelected: 2.5,
              });
            } catch (error) {
              // If style application fails, log for debugging but continue
              console.warn('Failed to apply grader color to annotation:', restoredAnnotation.annotationUID, error);
            }
          }
        }

        existingUIDs.add(ann.annotationUID); // Track newly added annotation
        restoredCount++;
      } catch (error) {
        // Error restoring individual annotation
      }
    }

    if (useGraderColors) {
      await applyCompareMarkerTextLinesToToolGroups();
    }

    // Trigger a render to display the annotations
    try {
      const { getRenderingEngines } = await import('@cornerstonejs/core');
      const renderingEngines = getRenderingEngines();

      if (renderingEngines && renderingEngines.length > 0) {
        renderingEngines.forEach((engine: any) => {
          const viewports = engine.getViewports();
          viewports.forEach((viewport: any) => {
            viewport.render();
          });
        });
      }
    } catch (error) {
      // Could not trigger render after restoring annotations
    }

    // After restoring, check for any annotations without graderId/color and fix them
    // This is a fallback in case the creation listener didn't catch them
    setTimeout(async () => {
      try {
        await fixAnnotationsWithoutGraderColor();
      } catch (error) {
        // Error fixing annotations
      }
    }, 500); // Small delay to ensure annotations are fully restored
  } catch (error) {
    // Error restoring annotations
  } finally {
    suppressAnnotationRemovalTracking = previousSuppressRemovalTracking;
    suppressAnnotationModificationTracking = previousSuppressModificationTracking;
  }
};

// Helper function to fix annotations that don't have graderId/color assigned
const fixAnnotationsWithoutGraderColor = async () => {
  try {
    const cornerstoneTools = window.cornerstoneTools || (await import('@cornerstonejs/tools')).default;
    if (!cornerstoneTools) {
      return;
    }

    const currentGraderId = (window as any).currentUserGraderId || currentUserGraderId;
    if (!currentGraderId) {
      return; // No graderId available
    }

    const allAnnotations = cornerstoneTools.annotation.state.getAllAnnotations();
    if (!allAnnotations || allAnnotations.length === 0) {
      return;
    }

    const normalizedGraderId = String(currentGraderId);
    const graderColor = getColorForGraderId(currentGraderId);

    let fixedCount = 0;
    for (const ann of allAnnotations) {
      // Skip if already has graderId or is ReferenceLines
      if (ann.metadata?.graderId || ann.metadata?.toolName === 'ReferenceLines') {
        continue;
      }

      // If this annotation already has a palette-selected color, don't override its style
      // (and don't set data.color which can cause confusing payloads).
      const hasPaletteColor = !!ann.data?.annotationColor || !!ann.metadata?.annotationColor;

      // Assign graderId and color
      if (!ann.metadata) {
        ann.metadata = {};
      }
      ann.metadata.graderId = normalizedGraderId;
      ann.metadata.graderColor = graderColor;

      if (!ann.data) {
        ann.data = {};
      }
      if (!hasPaletteColor) {
        ann.data.color = graderColor;
      }


      // Apply color style
      try {
        const toolsModule = await import('@cornerstonejs/tools');
        const csAnnotation = toolsModule.annotation;

        if (hasPaletteColor) {
          continue;
        }

        let colorString = graderColor;
        if (colorString.startsWith('#')) {
          const hex = colorString.slice(1);
          if (hex.length === 6) {
            const r = parseInt(hex.slice(0, 2), 16);
            const g = parseInt(hex.slice(2, 4), 16);
            const b = parseInt(hex.slice(4, 6), 16);
            colorString = `rgb(${r}, ${g}, ${b})`;
          }
        }

        csAnnotation.config.style.setAnnotationStyles(ann.annotationUID, {
          color: colorString,
          colorSelected: colorString,
          colorHighlighted: colorString,
          textBoxColor: colorString,
          textBoxColorSelected: colorString,
          textBoxColorHighlighted: colorString,
          lineWidth: 2.5,
          lineWidthSelected: 2.5,
        });

        fixedCount++;
        console.log(`[fixAnnotationsWithoutGraderColor] Fixed annotation ${ann.annotationUID} with color "${graderColor}"`);
      } catch (error) {
        console.warn(`[fixAnnotationsWithoutGraderColor] Failed to fix annotation ${ann.annotationUID}:`, error);
      }
    }

    if (fixedCount > 0) {
      console.log(`[fixAnnotationsWithoutGraderColor] Fixed ${fixedCount} annotations without graderId/color`);
      // Trigger render
      try {
        const { getRenderingEngines } = await import('@cornerstonejs/core');
        const renderingEngines = getRenderingEngines();
        if (renderingEngines && renderingEngines.length > 0) {
          renderingEngines.forEach((engine: any) => {
            const viewports = engine.getViewports();
            viewports.forEach((viewport: any) => {
              viewport.render();
            });
          });
        }
      } catch (renderError) {
        // Could not trigger render
      }
    }
  } catch (error) {
    console.warn('[fixAnnotationsWithoutGraderColor] Error fixing annotations:', error);
  }
};

// Floating Form Component
/** Clears in-component study context so toolbar save cannot reuse a previous study. */
function clearFloatingFormStudyContext(setters: {
  setProtocol: (v: Protocol | null) => void;
  setAnnotationData: (v: AnnotationData | null) => void;
  setFormData: (v: Record<string, any>) => void;
  setSavedAnnotationId: (v: number | null) => void;
  setStudyId: (v: number | null) => void;
  setStudyUid: (v: string | null) => void;
  setGraderId: (v: number | null) => void;
  setAnnotationStatus: (v: string | null) => void;
  setSaving: (v: boolean) => void;
  setLoading: (v: boolean) => void;
}) {
  resetFloatingFormBaseline();
  setters.setProtocol(null);
  setters.setAnnotationData(null);
  setters.setFormData({});
  setters.setSavedAnnotationId(null);
  setters.setStudyId(null);
  setters.setStudyUid(null);
  setters.setGraderId(null);
  setters.setAnnotationStatus(null);
  setters.setSaving(false);
  setters.setLoading(false);
  currentUserGraderId = null;
  if (typeof window !== 'undefined') {
    (window as any).currentUserGraderId = null;
  }
}

const FloatingGradingForm: React.FC<{ isVisible: boolean; instanceUid: string | null; onClose: () => void }> = ({
  isVisible,
  instanceUid,
  onClose,
}) => {
  // Initialize position to right side of screen
  const [position, setPosition] = useState<Position>(() => ({
    x: window.innerWidth - 450, // Align to right with some margin
    y: 50
  }));
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState<Position>({ x: 0, y: 0 });
  const formRef = useRef<HTMLDivElement>(null);
  // Used to detect true "study change" vs initial mount/open transition (e.g. null -> uid).
  const lastInstanceUidRef = useRef<string | null>(null);
  const [protocol, setProtocol] = useState<Protocol | null>(null);
  const [annotationData, setAnnotationData] = useState<AnnotationData | null>(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);
  const [savedGradingId, setSavedGradingId] = useState<number | null>(null);
  const [savedAnnotationId, setSavedAnnotationId] = useState<number | null>(null);
  const [studyId, setStudyId] = useState<number | null>(null);
  const [studyUid, setStudyUid] = useState<string | null>(null); // Add study_uid for API calls
  const [graderId, setGraderId] = useState<number | null>(null);
  const [annotationStatus, setAnnotationStatus] = useState<string | null>(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [isViewOnlyUser, setIsViewOnlyUser] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem('isViewOnly') === 'true';
    } catch {
      return false;
    }
  });
  const [showSubmitConfirmation, setShowSubmitConfirmation] = useState(false);
  const [showDraftConfirmation, setShowDraftConfirmation] = useState(false);
  const [showEmptyFormWarning, setShowEmptyFormWarning] = useState(false);
  const [showStudyCompletionModal, setShowStudyCompletionModal] = useState(false);
  const [fieldCountInfo, setFieldCountInfo] = useState<{ filled: number; total: number } | null>(null);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const getParameterDisplayName = (parameter: Parameter): string => {
    const fallbackName = (parameter as any)?.name;
    return (parameter.parameter_name || fallbackName || 'Parameter') as string;
  };

  const FORM_FIELD_LABELS = {
    NUMERIC: 'Enter measured value',
    DROPDOWN: 'Select clinical classification',
    CHECKBOX_DEFAULT: 'Select all that apply',
    CHECKBOX_FEATURES: 'Identify observed features',
    FREETEXT_DEFAULT: 'Describe specific findings',
    FREETEXT_CLINICAL: 'Detail clinical findings',
    REMARKS: 'Enter additional remarks',
  } as const;

  const getFieldDisplayLabel = (field: ParameterField): string => {
    const rawLabel = String(field.label ?? '').trim();
    const lowerLabel = rawLabel.toLowerCase();
    const elementType = String(field.element_type ?? '').toLowerCase();

    // Numeric label in UI is determined by label text (existing behavior),
    // but we replace the displayed label with a constant for consistency.
    if (lowerLabel === 'numeric') {
      return FORM_FIELD_LABELS.NUMERIC;
    }

    if (elementType === 'dropdown') {
      return FORM_FIELD_LABELS.DROPDOWN;
    }

    if (elementType === 'checkbox') {
      // Choose between the two checkbox phrasings based on the field's API label.
      return lowerLabel.includes('feature')
        ? FORM_FIELD_LABELS.CHECKBOX_FEATURES
        : FORM_FIELD_LABELS.CHECKBOX_DEFAULT;
    }

    // Textbox fields cover both free-text and remarks (textarea) behaviors.
    if (elementType === 'textbox') {
      if (
        lowerLabel.includes('remarks') ||
        lowerLabel.includes('notes') ||
        lowerLabel.includes('description')
      ) {
        return FORM_FIELD_LABELS.REMARKS;
      }

      // Choose between the two free-text phrasings based on the field's API label.
      return lowerLabel.includes('clinical')
        ? FORM_FIELD_LABELS.FREETEXT_CLINICAL
        : FORM_FIELD_LABELS.FREETEXT_DEFAULT;
    }

    // Fallback to protocol-provided label for any other types.
    return rawLabel || 'Field';
  };


  // Get service instances
  const gradingService = GradingService.getInstance();

  const notification = useNotification();

  // Load annotation data and protocol
  const loadAnnotationData = async (instanceUid: string) => {

    // Read current view-only flag from sessionStorage
    let viewOnly = false;
    try {
      viewOnly = sessionStorage.getItem('isViewOnly') === 'true';
    } catch {
      viewOnly = false;
    }
    setIsViewOnlyUser(viewOnly);

    // Reset ALL form state variables before loading new data
    resetFloatingFormBaseline();
    setProtocol(null);
    setAnnotationData(null);
    setFormData({});
    setSavedAnnotationId(null);
    setStudyId(null);
    setStudyUid(null);
    setGraderId(null);
    // Also clear globally
    currentUserGraderId = null;
    if (typeof window !== 'undefined') {
      (window as any).currentUserGraderId = null;
    }
    setAnnotationStatus(null);
    // If the user is view-only, keep the form in read-only mode
    setIsReadOnly(viewOnly);
    setLoading(true);
    setSaving(false);

    try {
      let response;

      // Check if data is already loaded from viewer initialization
      let isFreshApiCall = false;
      if (window.annotationsData) {

        // Filter out deleted annotations from cached window.annotationsData before using it
        if (window.annotationsData?.data?.[0]?.annotations?.[0]?.data) {
          const dataObj = window.annotationsData.data[0].annotations[0].data;
          let annotationsData = null;

          if (dataObj?.rawdata) {
            annotationsData = dataObj.rawdata;
          } else if (dataObj?.annotations) {
            let parsed = typeof dataObj.annotations === 'string' ? JSON.parse(dataObj.annotations) : dataObj.annotations;
            if (parsed.annotations && typeof parsed.annotations === 'object' && parsed.annotations.annotations) {
              parsed = parsed.annotations;
            }
            annotationsData = parsed;
          }

          if (annotationsData?.annotations) {
            const beforeCount = annotationsData.annotations.length;
            annotationsData.annotations = annotationsData.annotations.filter((ann: any) => {
              // Filter out deleted annotations
              if (deletedAnnotationUIDs.has(ann.annotationUID)) {
                return false;
              }
              // Filter out ReferenceLines
              if (ann.metadata?.toolName === 'ReferenceLines') {
                return false;
              }
              return true;
            });
            const afterCount = annotationsData.annotations.length;

            if (beforeCount !== afterCount) {

              // Update the cached data structure
              if (dataObj?.rawdata) {
                dataObj.rawdata = annotationsData;
              } else if (dataObj?.annotations) {
                dataObj.annotations = typeof dataObj.annotations === 'string'
                  ? JSON.stringify(annotationsData)
                  : annotationsData;
              }
            }
          }
        }

        response = window.annotationsData;
      } else {
        // Fallback: fetch from API if not pre-loaded using valid endpoint
        isFreshApiCall = true;

        // Get study_uid from sessionStorage or use provided instanceUid
        const apiStudyUid = sessionStorage.getItem('currentStudyUid') || instanceUid;
        if (!apiStudyUid) {
          throw new Error('No study_uid available for API call');
        }

        // Make direct API call to the valid endpoint
        const { default: ApiService } = await import('../../../platform/app/src/services/ApiService');
        const apiService = ApiService.getInstance();
        const baseUrl = apiService.getGradingServiceUrl();
        const endpoint = `/annotations/study/${apiStudyUid}`;

        // Build query parameters - include job_id if available
        const queryParams: Record<string, any> = {};
        const secureStorage = SecureStorageService.getInstance();
        let jobId: string | null = null;
        try {
          jobId = await secureStorage.getSessionItem('currentJobId');
        } catch (error) {
          // Fallback to unencrypted for migration
          jobId = sessionStorage.getItem('currentJobId');
        }
        if (jobId) {
          const jobIdNumber = parseInt(jobId, 10);
          if (jobIdNumber && jobIdNumber !== 0) {
            queryParams.job_id = jobIdNumber;
          }
        }

        const apiResponse = await apiService.get(endpoint, Object.keys(queryParams).length > 0 ? queryParams : undefined, baseUrl);

        if (apiResponse.success && apiResponse.data) {
          response = apiResponse.data;
        } else {
          throw new Error(apiResponse.error || 'Failed to fetch annotations');
        }
      }

      // Fresh API data: drop "pending delete" tracking only when the server no longer returns
      // that annotation (delete was persisted). If the user deleted locally but has not saved yet,
      // the annotation may still appear in the API — we must NOT remove its UID from
      // deletedAnnotationUIDs or it will be restored from cached/API data and reappear on screen.
      if (isFreshApiCall && response?.data?.[0]?.annotations?.[0]?.data) {
        const dataObj = response.data[0].annotations[0].data;
        let annotationsData = null;

        if (dataObj?.rawdata) {
          annotationsData = dataObj.rawdata;
        } else if (dataObj?.annotations) {
          let parsed = typeof dataObj.annotations === 'string' ? JSON.parse(dataObj.annotations) : dataObj.annotations;
          if (parsed.annotations && typeof parsed.annotations === 'object' && parsed.annotations.annotations) {
            parsed = parsed.annotations;
          }
          annotationsData = parsed;
        }

        if (annotationsData?.annotations && Array.isArray(annotationsData.annotations)) {
          const apiAnnotationUIDs = new Set(
            annotationsData.annotations.map((ann: any) => ann.annotationUID).filter(Boolean)
          );

          const uidsResolvedOnServer = Array.from(deletedAnnotationUIDs).filter(
            uid => !apiAnnotationUIDs.has(uid)
          );
          uidsResolvedOnServer.forEach(uid => deletedAnnotationUIDs.delete(uid));
        }
      }

      if (response.is_success && response.data && response.data.length > 0) {
        // Keep window snapshot in sync for navigation unsaved checks (opening the form
        // must not leave canvas UIDs unmatched with an empty global).
        window.annotationsData = response as AnnotationResponse;

        const data = response.data[0];
        setAnnotationData(data);
        setProtocol(data.protocol);

        // Extract study_id, study_uid from API response (study may have assignees array, no grader_id)
        if (data.study) {
          setStudyId(data.study.id);
          setStudyUid(data.study.study_uid || data.instance_uid); // Store study_uid for API calls
          if (data.study.job_id) {
            const secureStorage = SecureStorageService.getInstance();
            secureStorage.setSessionItem('currentJobId', data.study.job_id.toString()).catch(err => {
              console.error('[FloatingForm] Failed to encrypt job ID, using unencrypted fallback:', err);
              sessionStorage.setItem('currentJobId', data.study.job_id.toString());
            });
          }
        }

        // Grader id = logged-in user (study no longer has grader_id; use current user for save/annotations)
        let currentUserId: string | null = null;
        try {
          const { default: AuthService } = await import('../../../platform/app/src/services/AuthService');
          const authService = AuthService.getInstance();
          const currentUser = authService.getCurrentUser();
          if (currentUser?.id != null) {
            currentUserId = String(currentUser.id);
            setGraderId(Number(currentUser.id));
            currentUserGraderId = currentUser.id;
            if (typeof window !== 'undefined') {
              (window as any).currentUserGraderId = currentUser.id;
            }
            console.log('[FloatingGradingForm] Set graderId from logged-in user:', currentUser.id);
          }
        } catch (error) {
          console.error('[FloatingForm] Error getting current user:', error);
        }

        // Select annotation by logged-in grader_id (from annotations array, not study)
        if (data.annotations && data.annotations.length > 0) {
          let selectedAnnotation: any = null;
          let shouldShowEmptyForm = false;

          if (currentUserId != null) {
            const ownAnnotation = data.annotations.find(
              (ann: any) => String(ann.grader_id) === currentUserId
            );
            if (ownAnnotation) {
              selectedAnnotation = ownAnnotation;
            } else {
              // No annotation for this grader - show plain form, save as new annotation
              shouldShowEmptyForm = true;
            }
          } else {
            shouldShowEmptyForm = true;
          }

          if (shouldShowEmptyForm) {
            setSavedAnnotationId(null);
            initializeFormData(data.protocol);
            setLoading(false);
            return;
          }

          // Load existing annotations into form using selected annotation

          // Extract annotation_id for updates
          if (selectedAnnotation.id) {
            setSavedAnnotationId(selectedAnnotation.id);
          }

          // Check annotation status for read-only mode
          const status = selectedAnnotation.status;
          setAnnotationStatus(status);
          const isSubmitted = status && status.toLowerCase() === 'submitted';
          const effectiveReadOnly = isSubmitted || viewOnly;
          setIsReadOnly(effectiveReadOnly);

          // Check if we have parameters array in annotations
          if (selectedAnnotation.parameters && selectedAnnotation.parameters.length > 0) {

            // Convert parameters array to form data format
            const formDataFromAnnotations: Record<string, any> = {};

            // Helper function to find field definition by field_id
            const findFieldDefinition = (fieldId: number, protocol: any): any => {
              const findFieldInParameters = (params: any[]): any => {
                for (const param of params) {
                  // Check parameter_fields
                  if (param.parameter_fields) {
                    for (const field of param.parameter_fields) {
                      if (field.id === fieldId) {
                        return field;
                      }
                    }
                  }

                  // Check children
                  if (param.children && param.children.length > 0) {
                    const childField = findFieldInParameters(param.children);
                    if (childField) return childField;
                  }
                }
                return null;
              };

              return findFieldInParameters(protocol.parameters);
            };

            // Create a mapping of field_id to form field key
            const createFormFieldKey = (fieldId: number, protocol: any) => {
              // Search through protocol parameters to find the field and build the correct key
              const findFieldInParameters = (params: any[], parentKey = ''): string | null => {
                for (const param of params) {
                  const key = parentKey ? `${parentKey}_${param.id}` : param.id.toString();

                  // Check parameter_fields
                  if (param.parameter_fields) {
                    for (const field of param.parameter_fields) {
                      if (field.id === fieldId) {
                        return `${key}_field_${field.id}`;
                      }
                    }
                  }

                  // Check children
                  if (param.children && param.children.length > 0) {
                    const childResult = findFieldInParameters(param.children, key);
                    if (childResult) return childResult;
                  }
                }
                return null;
              };

              return findFieldInParameters(protocol.parameters);
            };

            // Map parameters to form data
            selectedAnnotation.parameters.forEach((param: any) => {
              const fieldKey = createFormFieldKey(param.field_id, data.protocol);
              if (fieldKey) {
                // Find the field definition to check if it's a checkbox
                const fieldDef = findFieldDefinition(param.field_id, data.protocol);
                const isCheckbox = fieldDef && fieldDef.element_type?.toLowerCase() === 'checkbox';

                // Handle different value types
                if (isCheckbox) {
                  // For checkboxes, always convert to array (even single values)
                  if (param.value.includes(',')) {
                    // Multiple values (comma-separated)
                    formDataFromAnnotations[fieldKey] = param.value.split(',').map((v: string) => v.trim());
                  } else {
                    // Single checkbox value - convert to array
                    formDataFromAnnotations[fieldKey] = [param.value.trim()];
                  }
                } else {
                  // For non-checkbox fields, keep as string
                  formDataFromAnnotations[fieldKey] = param.value;
                }
              }
            });

            setFormData(formDataFromAnnotations);
            captureFloatingFormBaseline(formDataFromAnnotations);

            // Restore Cornerstone annotations if they exist in the data field
            if (selectedAnnotation.data) {

              const dataObj = selectedAnnotation.data;
              let annotationsData = null;

              // Check for rawdata field (new format)
              if (dataObj?.rawdata) {
                // If rawdata is a string, parse it first
                if (typeof dataObj.rawdata === 'string') {
                  annotationsData = JSON.parse(dataObj.rawdata);
                } else {
                  annotationsData = dataObj.rawdata;
                }
              }
              // Fallback to old format for backwards compatibility
              else if (dataObj?.annotations) {
                // Parse the stringified JSON
                let parsed = typeof dataObj.annotations === 'string' ? JSON.parse(dataObj.annotations) : dataObj.annotations;

                // Check for double-nested structure (old saves)
                if (parsed.annotations && typeof parsed.annotations === 'object' && parsed.annotations.annotations) {
                  parsed = parsed.annotations;
                }

                annotationsData = parsed;
              } else if (typeof dataObj === 'string') {
                // Old format: stringified directly
                annotationsData = JSON.parse(dataObj);
              } else {
                // Direct object
                annotationsData = dataObj;
              }


              // CRITICAL: Create a deep copy to avoid reference issues, then filter
              // This ensures we're working with a fresh copy that we can safely modify
              let filteredAnnotationsData = null;
              if (annotationsData) {
                filteredAnnotationsData = JSON.parse(JSON.stringify(annotationsData));
              }

              // CRITICAL: Filter out deleted annotations and ReferenceLines AFTER parsing
              // This must happen after parsing because parsing creates new objects
              if (filteredAnnotationsData?.annotations) {
                const beforeCount = filteredAnnotationsData.annotations.length;
                const beforeUIDs = filteredAnnotationsData.annotations.map((ann: any) => ann.annotationUID);
                filteredAnnotationsData.annotations = filteredAnnotationsData.annotations.filter((ann: any) => {
                  // Filter out deleted annotations
                  if (deletedAnnotationUIDs.has(ann.annotationUID)) {
                    return false;
                  }
                  // Filter out ReferenceLines (helper/guide lines)
                  if (ann.metadata?.toolName === 'ReferenceLines') {
                    return false;
                  }
                  return true;
                });
                const afterCount = filteredAnnotationsData.annotations.length;
                const afterUIDs = filteredAnnotationsData.annotations.map((ann: any) => ann.annotationUID);

                if (beforeCount !== afterCount) {
                } else {
                }
              }

              // Use the filtered copy
              annotationsData = filteredAnnotationsData;

              // Final safety check: Ensure annotationsData doesn't contain deleted annotations
              // This is critical because the setTimeout delay might allow state changes
              if (annotationsData?.annotations) {
                const finalCheck = annotationsData.annotations.filter((ann: any) => {
                  if (deletedAnnotationUIDs.has(ann.annotationUID)) {
                    return false;
                  }
                  if (ann.metadata?.toolName === 'ReferenceLines') {
                    return false;
                  }
                  return true;
                });

                if (finalCheck.length !== annotationsData.annotations.length) {
                  annotationsData.annotations = finalCheck;
                }
              }

              // Use setTimeout to ensure the viewport is fully loaded before restoring
              setTimeout(() => {
                // One more check right before restoration (defensive programming)
                if (annotationsData?.annotations) {
                  const preRestoreCheck = annotationsData.annotations.filter((ann: any) => {
                    if (deletedAnnotationUIDs.has(ann.annotationUID)) {
                      return false;
                    }
                    if (ann.metadata?.toolName === 'ReferenceLines') {
                      return false;
                    }
                    return true;
                  });
                  // Apply current grader visibility state.
                  // Without this, opening the floating form re-restores hidden grader annotations
                  // a bit later (matches the "after few seconds" bug report).
                  const filteredByGraderVisibility = preRestoreCheck.filter((ann: any) => {
                    const annGraderId = ann.metadata?.graderId;
                    if (!annGraderId) {
                      // If we don't know ownership, hide (consistent with toggle filtering).
                      return false;
                    }
                    const normalizedAnnGraderId = String(annGraderId);
                    // If explicitly hidden -> hide; otherwise default to visible.
                    return visibleGraders.get(normalizedAnnGraderId) !== false;
                  });

                  annotationsData.annotations = filteredByGraderVisibility;
                }
                restoreCornerstoneAnnotations(annotationsData);
              }, 1500);
            }
          } else {
            initializeFormData(data.protocol);
          }
        } else {
          // No annotations - show plain form, save as new annotation
          setSavedAnnotationId(null);
          initializeFormData(data.protocol);
        }
      }
    } catch (error) {
      // Error loading annotation data
    } finally {
      setLoading(false);
    }
  };

  // Initialize form data structure based on parameter_fields
  const initializeFormData = (protocolData: Protocol) => {
    const initialData: Record<string, any> = {};

    const processParameter = (param: Parameter, parentKey = '') => {
      const key = parentKey ? `${parentKey}_${param.id}` : param.id.toString();

      // If has_child is true, process children's parameter_fields
      if (param.has_child && param.children.length > 0) {
        param.children.forEach(child => {
          const childKey = `${key}_${child.id}`;
          // Initialize fields for each child based on their parameter_fields
          child.parameter_fields.forEach(field => {
            const fieldKey = `${childKey}_field_${field.id}`;
            // Initialize based on field type
            if (field.element_type === 'CheckBox') {
              initialData[fieldKey] = [];
            } else {
              initialData[fieldKey] = '';
            }
          });
        });
      } else {
        // If has_child is false, initialize fields directly from parameter_fields
        param.parameter_fields.forEach(field => {
          const fieldKey = `${key}_field_${field.id}`;
          // Initialize based on field type
          if (field.element_type === 'CheckBox') {
            initialData[fieldKey] = [];
          } else {
            initialData[fieldKey] = '';
          }
        });
      }
    };

    protocolData.parameters.forEach(param => processParameter(param));
    setFormData(initialData);
    captureFloatingFormBaseline(initialData);
  };

  // Reset form state when instanceUid changes (new study)
  useEffect(() => {
    if (!instanceUid) {
      return;
    }

    const prevInstanceUid = lastInstanceUidRef.current;
    const isNewStudy = !!prevInstanceUid && prevInstanceUid !== instanceUid;
    lastInstanceUidRef.current = instanceUid;

    // Reset form study context on any study switch (including when the form is hidden).
    if (isNewStudy) {
      clearFloatingFormStudyContext({
        setProtocol,
        setAnnotationData,
        setFormData,
        setSavedAnnotationId,
        setStudyId,
        setStudyUid,
        setGraderId,
        setAnnotationStatus,
        setSaving,
        setLoading,
      });
      setIsReadOnly(false);
    }

    if (isNewStudy) {
      // Only clear global state when switching studies (not on initial open mount).
      currentUserGraderId = null;
      if (typeof window !== 'undefined') {
        (window as any).currentUserGraderId = null;
      }
      // Clear deleted annotations tracking for new study
      deletedAnnotationUIDs.clear();
      modifiedAnnotationUIDs.clear();

      // Clear grader visibility state for new study - all graders should be visible by default
      visibleGraders.clear();
      if (typeof window !== 'undefined') {
        (window as any).visibleGraders = visibleGraders;
      }
    }
  }, [instanceUid, isVisible]);

  // Study changes are detected in FloatingFormNavigationTracker (inside Router) and routed here.
  useEffect(() => {
    const resetStudyContext = (activeStudyUid: string) => {
      clearFloatingFormStudyContext({
        setProtocol,
        setAnnotationData,
        setFormData,
        setSavedAnnotationId,
        setStudyId,
        setStudyUid,
        setGraderId,
        setAnnotationStatus,
        setSaving,
        setLoading,
      });
      setIsReadOnly(false);
      lastInstanceUidRef.current = activeStudyUid;
    };

    floatingFormStore.registerStudyContextReset(resetStudyContext);
    return () => floatingFormStore.unregisterStudyContextReset();
  }, []);

  // Ensure global annotation listeners are set up when component mounts
  useEffect(() => {
    setupGlobalAnnotationDeletionListener();
    setupGlobalAnnotationModificationListener();
  }, []);

  // Load annotation data when form becomes visible
  useEffect(() => {
    if (isVisible) {

      // Always try to load data when form becomes visible
      if (window.annotationsData) {
        loadAnnotationData(''); // Empty string since we're using pre-loaded data
      } else {
        // Always try to fetch from API if no pre-loaded data
        loadAnnotationData(instanceUid || '');
      }
    }
  }, [isVisible, instanceUid]);

  useEffect(() => {
    if (!isVisible) {
      resetFloatingFormBaseline();
      return;
    }
    if (!floatingFormBaselineCaptured) {
      return;
    }
    (window as any).floatingFormDataCurrent = serializeFormDataForCompare(formData);
  }, [formData, isVisible]);

  // Reposition form to right side on window resize
  useEffect(() => {
    const handleResize = () => {
      setPosition(prev => ({
        x: window.innerWidth - 450, // Keep aligned to right
        y: prev.y
      }));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (formRef.current) {
      const rect = formRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging && formRef.current) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      const maxX = window.innerWidth - (formRef.current.offsetWidth || 440);
      const maxY = window.innerHeight - (formRef.current.offsetHeight || 300);

      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  // New API integration for save draft and submit
  const saveAnnotation = async (
    status: 'Draft' | 'Submit',
    options?: { skipFormValidation?: boolean; fromNavigation?: boolean }
  ): Promise<boolean> => {
    if (!protocol || !annotationData) {
      if (!options?.fromNavigation) {
        notification?.show?.({
          title: 'Unable to save',
          message: 'No protocol or annotation data loaded',
          type: 'error',
          position: 'top-right',
          duration: 4000,
        });
      }
      return false;
    }

    const activeStudyUid = await getActiveViewerStudyUid();
    const formStudyUid = studyUid || instanceUid || null;
    const globalAnnotations = window.annotationsData;

    const formContextMatchesActiveStudy =
      (!activeStudyUid || !formStudyUid || formStudyUid === activeStudyUid) &&
      isAnnotationResponseForStudyUid(globalAnnotations, activeStudyUid);

    if (!formContextMatchesActiveStudy) {
      if (options?.fromNavigation) {
        const { saveDraftAnnotationsWithActivity } = await import(
          '../../../extensions/cornerstone/src/utils/annotationSaveUtils'
        );
        return saveDraftAnnotationsWithActivity();
      }
      notification?.show?.({
        title: 'Unable to save',
        message: 'Study context is still loading. Please wait a moment and try again.',
        type: 'error',
        position: 'top-right',
        duration: 4000,
      });
      return false;
    }

    // Save only if at least one field is filled (same rule as earlier)
    if (!options?.skipFormValidation && !hasAtLeastOneField()) {
      setShowEmptyFormWarning(true);
      return false;
    }

    setSaving(true);
    try {
      // Import ApiService for the new API call
      const { default: ApiService } = await import('../../../platform/app/src/services/ApiService');
      const apiService = ApiService.getInstance();
      const baseUrl = apiService.getGradingServiceUrl();
      const endpoint = '/annotations';

      // Transform form data into parameters array format
      const parameters = Object.entries(formData)
        .filter(([key, value]) => value !== '' && value !== null && value !== undefined)
        .map(([key, value]) => {
          // Extract field_id from the key (format: "baseKey_field_123")
          const fieldIdMatch = key.match(/_field_(\d+)$/);
          const fieldId = fieldIdMatch ? parseInt(fieldIdMatch[1]) : null;

          // Convert value to string for API
          let stringValue = '';
          if (Array.isArray(value)) {
            // For checkboxes, join multiple values with comma
            stringValue = value.join(', ');
      } else {
            stringValue = String(value);
          }

          return {
            field_id: fieldId,
            value: stringValue
          };
        })
        .filter(param => param.field_id !== null); // Only include valid field_ids

      // Get job_id from secure storage (encrypted)
      const secureStorage = SecureStorageService.getInstance();
      let jobId: string | null = null;
      try {
        jobId = await secureStorage.getSessionItem('currentJobId');
      } catch (error) {
        // Fallback to unencrypted for migration
        jobId = sessionStorage.getItem('currentJobId');
      }
      const jobIdNumber = jobId ? parseInt(jobId, 10) : 0;

      // Resolve grader_id from multiple sources (state can be stale or not yet set)
      let effectiveGraderId: number | string | null =
        graderId ??
        currentUserGraderId ??
        (typeof window !== 'undefined' ? (window as any).currentUserGraderId : null) ??
        (annotationData as any)?.study?.grader_id ??
        (annotationData as any)?.study_info?.grader_id ??
        null;
      if (effectiveGraderId == null) {
        try {
          const { default: AuthService } = await import('../../../platform/app/src/services/AuthService');
          const authService = AuthService.getInstance();
          const currentUser = authService.getCurrentUser();
          if (currentUser?.id != null) {
            effectiveGraderId = currentUser.id;
          }
        } catch {
          // ignore
        }
      }
      const graderIdForPayload = effectiveGraderId != null ? Number(effectiveGraderId) : null;
      if (graderIdForPayload == null || graderIdForPayload === 0) {
        if (!options?.fromNavigation) {
          notification?.show?.({
            title: 'Unable to save',
            message: 'Grader could not be determined. Please refresh and try again.',
            type: 'error',
            position: 'top-right',
            duration: 4000,
          });
        }
        setSaving(false);
        return false;
      }

      const currentUserIdForPayload =
        graderIdForPayload != null ? String(graderIdForPayload) : null;

      // Capture Cornerstone annotations before saving
      // Pass effectiveGraderId to filter annotations - only save annotations belonging to the current user
      // This is critical when admin/manager is viewing merged annotations from multiple graders
      const cornerstoneAnnotations = await captureCornerstoneAnnotations(effectiveGraderId);

      // Resolve study_id from the current global annotations snapshot (not stale React state).
      let payloadStudyId = studyId || 0;
      let payloadAnnotationId = savedAnnotationId;
      const globalFirst = globalAnnotations?.data?.[0] as
        | { study?: { id?: number }; annotations?: Array<{ id?: number; grader_id?: number }> }
        | undefined;
      if (globalFirst?.study?.id != null) {
        payloadStudyId = globalFirst.study.id;
      }

      if (currentUserIdForPayload != null && globalFirst?.annotations?.length) {
        const ownRow = globalFirst.annotations.find(
          (ann: { grader_id?: number }) => String(ann.grader_id) === currentUserIdForPayload
        );
        if (ownRow?.id != null) {
          payloadAnnotationId = ownRow.id;
        }
      }

      // Prepare payload according to new API structure
      // If no annotations, send empty structure with empty annotations array
      const dataPayload = cornerstoneAnnotations
        ? { rawdata: cornerstoneAnnotations }
        : { rawdata: { annotations: [], timestamp: new Date().toISOString() } };

      const payload = {
        // Include annotation_id if we have a saved annotation (for updates)
        ...(payloadAnnotationId && { annotation_id: payloadAnnotationId }),
        study_id: payloadStudyId,
        grader_id: graderIdForPayload,
        job_id: jobIdNumber,
        data: dataPayload,
        parameters: parameters,
        status: status === 'Submit' ? 'SUBMITTED' : status.toUpperCase(), // Convert to uppercase (DRAFT/SUBMITTED)
      };

      if (savedAnnotationId) {
      } else {
      }

      // Make API call using ApiService.post()
      const response = await apiService.post(endpoint, payload, baseUrl);

      if (response.success && response.data) {
        const { clearAnnotationTrackingState } = await import(
          '../../../extensions/cornerstone/src/utils/annotationSaveUtils'
        );
        clearAnnotationTrackingState();

        // Store the annotation_id for future updates
        const responseData = response.data as any;
        let savedIdFromResponse =
          extractAnnotationIdFromSaveResponse(responseData) ??
          normalizeAnnotationId(savedAnnotationId);

        if (!savedIdFromResponse) {
          const apiStudyUid =
            studyUid || window.currentStudyUid || sessionStorage.getItem('currentStudyUid');
          if (apiStudyUid) {
            savedIdFromResponse = await fetchAnnotationIdForGrader(
              apiStudyUid,
              graderIdForPayload,
              jobIdNumber
            );
          }
        }

        if (savedIdFromResponse) {
          setSavedAnnotationId(savedIdFromResponse);
        }

        const action = status === 'Draft' ? 'saved' : 'submitted';

        // Log activity for this save using timer delta (use id from save response on first save)
        try {
          const activityService = ActivityService.getInstance();
          await activityService.logActivity('save', savedIdFromResponse);
        } catch (activityError) {
          // Failed to log save activity
        }

        // Close the floating form immediately after successful save (not when saving before navigation)
        if (!options?.fromNavigation) {
          onClose();
        }

        // Update isViewOnly in sessionStorage when study is completed (submitted)
        // This allows the timer to efficiently change to review mode
        if (status === 'Submit') {
          try {
            sessionStorage.setItem('isViewOnly', 'true');
            setIsViewOnlyUser(true);
          } catch (error) {
            console.warn('[FloatingForm] Failed to update isViewOnly in sessionStorage:', error);
          }
          setShowStudyCompletionModal(true);
        } else if (!options?.fromNavigation) {
          // Show notification on the main DICOM viewer page for Draft saves
          notification?.show?.({
            title: 'Draft saved',
            message: 'Form saved successfully',
            type: 'success',
            duration: 3000,
            position: 'top-right',
          });
        }

        // Reload annotation data to get updated information
        try {
          // Get the study_uid from state (DICOM UID string, not the numeric ID)
          const apiStudyUid = studyUid || window.currentStudyUid || sessionStorage.getItem('currentStudyUid');
          if (apiStudyUid) {
            // Clear pre-loaded data to force fresh API call
            window.annotationsData = undefined;

            // Make direct API call to the correct endpoint
            const { default: ApiService } = await import('../../../platform/app/src/services/ApiService');
            const apiService = ApiService.getInstance();
            const baseUrl = apiService.getGradingServiceUrl();
            const endpoint = `/annotations/study/${apiStudyUid}`;

            // Build query parameters - include job_id if available
            const queryParams: Record<string, any> = {};
            if (jobId && jobIdNumber !== 0) {
              queryParams.job_id = jobIdNumber;
            }

            const reloadResponse = await apiService.get(endpoint, Object.keys(queryParams).length > 0 ? queryParams : undefined, baseUrl);

            if (reloadResponse.success && reloadResponse.data) {
              window.annotationsData = reloadResponse.data as AnnotationResponse;

              const { clearAnnotationTrackingState } = await import(
                '../../../extensions/cornerstone/src/utils/annotationSaveUtils'
              );
              clearAnnotationTrackingState();

              // Reset ALL form state variables before processing reloaded data
              setProtocol(null);
              setAnnotationData(null);
              setFormData({});
              setSavedAnnotationId(null);
              setStudyId(null);
              setStudyUid(null);
              setGraderId(null);
    // Also clear globally
    currentUserGraderId = null;
    if (typeof window !== 'undefined') {
      (window as any).currentUserGraderId = null;
    }
              setAnnotationStatus(null);
              setIsReadOnly(false);

              // Process the reloaded data
              const responseData = reloadResponse.data as any;
              if (responseData.is_success && responseData.data && responseData.data.length > 0) {
                const data = responseData.data[0];
                setAnnotationData(data);
                setProtocol(data.protocol);

                // Extract study_id, study_uid (study may have assignees array, no grader_id)
                if (data.study) {
                  setStudyId(data.study.id);
                  setStudyUid(data.study.study_uid || data.instance_uid);
                }

                // Grader id = logged-in user
                let reloadCurrentUserId: string | null = null;
                try {
                  const { default: AuthService } = await import('../../../platform/app/src/services/AuthService');
                  const authService = AuthService.getInstance();
                  const currentUser = authService.getCurrentUser();
                  if (currentUser?.id != null) {
                    reloadCurrentUserId = String(currentUser.id);
                    setGraderId(Number(currentUser.id));
                    currentUserGraderId = currentUser.id;
                    if (typeof window !== 'undefined') {
                      (window as any).currentUserGraderId = currentUser.id;
                    }
                  }
                } catch (error) {
                  console.error('[FloatingForm] Error getting current user during reload:', error);
                }

                // Select annotation by logged-in grader_id (from annotations array)
                if (!data.annotations || data.annotations.length === 0) {
                  setSavedAnnotationId(null);
                  initializeFormData(data.protocol);
                } else {
                  let selectedAnnotation: any = null;
                  let shouldShowEmptyForm = false;

                  if (reloadCurrentUserId != null) {
                    const ownAnnotation = data.annotations.find(
                      (ann: any) => String(ann.grader_id) === reloadCurrentUserId
                    );
                    if (ownAnnotation) {
                      selectedAnnotation = ownAnnotation;
                    } else {
                      shouldShowEmptyForm = true;
                    }
                  } else {
                    shouldShowEmptyForm = true;
                  }

                  if (shouldShowEmptyForm) {
                    setSavedAnnotationId(null);
                    initializeFormData(data.protocol);
                    return;
                  }

                  const status = selectedAnnotation.status;
                  setAnnotationStatus(status);
                  let viewOnly = false;
                  try {
                    viewOnly = sessionStorage.getItem('isViewOnly') === 'true';
                  } catch {
                    viewOnly = false;
                  }
                  setIsViewOnlyUser(viewOnly);

                  const isSubmitted = status && status.toLowerCase() === 'submitted';
                  const effectiveReadOnly = isSubmitted || viewOnly;
                  setIsReadOnly(effectiveReadOnly);

                  // Extract annotation_id
                  if (selectedAnnotation.id) {
                    setSavedAnnotationId(selectedAnnotation.id);
                  }

                  // Reload form data from parameters
                  if (selectedAnnotation.parameters && selectedAnnotation.parameters.length > 0) {
                    const formDataFromAnnotations: Record<string, any> = {};

                    // Helper function to find field definition by field_id
                    const findFieldDefinition = (fieldId: number, protocol: any): any => {
                      const findFieldInParameters = (params: any[]): any => {
                        for (const param of params) {
                          // Check parameter_fields
                          if (param.parameter_fields) {
                            for (const field of param.parameter_fields) {
                              if (field.id === fieldId) {
                                return field;
                              }
                            }
                          }

                          // Check children
                          if (param.children && param.children.length > 0) {
                            const childField = findFieldInParameters(param.children);
                            if (childField) return childField;
                          }
                        }
                        return null;
                      };

                      return findFieldInParameters(protocol.parameters);
                    };

                    // Create a mapping of field_id to form field key
                    const createFormFieldKey = (fieldId: number, protocol: any) => {
                      const findFieldInParameters = (params: any[], parentKey = ''): string | null => {
                        for (const param of params) {
                          const key = parentKey ? `${parentKey}_${param.id}` : param.id.toString();

                          if (param.parameter_fields) {
                            for (const field of param.parameter_fields) {
                              if (field.id === fieldId) {
                                return `${key}_field_${field.id}`;
                              }
                            }
                          }

                          if (param.children && param.children.length > 0) {
                            const childResult = findFieldInParameters(param.children, key);
                            if (childResult) return childResult;
                          }
                        }
                        return null;
                      };

                      return findFieldInParameters(protocol.parameters);
                    };

                    // Map parameters to form data
                    selectedAnnotation.parameters.forEach((param: any) => {
                      const fieldKey = createFormFieldKey(param.field_id, data.protocol);
                      if (fieldKey) {
                        // Find the field definition to check if it's a checkbox
                        const fieldDef = findFieldDefinition(param.field_id, data.protocol);
                        const isCheckbox = fieldDef && fieldDef.element_type?.toLowerCase() === 'checkbox';

                        // Handle different value types
                        if (isCheckbox) {
                          // For checkboxes, always convert to array (even single values)
                          if (param.value.includes(',')) {
                            // Multiple values (comma-separated)
                            formDataFromAnnotations[fieldKey] = param.value.split(',').map((v: string) => v.trim());
                          } else {
                            // Single checkbox value - convert to array
                            formDataFromAnnotations[fieldKey] = [param.value.trim()];
                          }
                        } else {
                          // For non-checkbox fields, keep as string
                          formDataFromAnnotations[fieldKey] = param.value;
                        }
                      }
                    });

                    setFormData(formDataFromAnnotations);
                    captureFloatingFormBaseline(formDataFromAnnotations);
                  }

                  // Restore Cornerstone annotations if they exist
                  if (selectedAnnotation.data) {

                    const dataObj = selectedAnnotation.data;
                    let annotationsData = null;

                    // Check for rawdata field (new format)
                    if (dataObj?.rawdata) {
                      annotationsData = dataObj.rawdata;
                    }
                    // Fallback to old format for backwards compatibility
                    else if (dataObj?.annotations) {
                      // Parse the stringified JSON
                      let parsed = typeof dataObj.annotations === 'string' ? JSON.parse(dataObj.annotations) : dataObj.annotations;

                      // Check for double-nested structure (old saves)
                      if (parsed.annotations && typeof parsed.annotations === 'object' && parsed.annotations.annotations) {
                        parsed = parsed.annotations;
                      }

                      annotationsData = parsed;
                    } else if (typeof dataObj === 'string') {
                      // Old format: stringified directly
                      annotationsData = JSON.parse(dataObj);
                    } else {
                      // Direct object
                      annotationsData = dataObj;
                    }


                    // Filter out deleted annotations and ReferenceLines before restoring
                    if (annotationsData?.annotations) {
                      const beforeCount = annotationsData.annotations.length;
                      annotationsData.annotations = annotationsData.annotations.filter((ann: any) => {
                        // Filter out deleted annotations
                        if (deletedAnnotationUIDs.has(ann.annotationUID)) {
                          return false;
                        }
                        // Filter out ReferenceLines (helper/guide lines)
                        if (ann.metadata?.toolName === 'ReferenceLines') {
                          return false;
                        }
                        return true;
                      });
                      const afterCount = annotationsData.annotations.length;
                      if (beforeCount !== afterCount) {
                      }
                    }

                    // Use setTimeout to ensure the viewport is fully loaded before restoring
                    setTimeout(() => {
                      // Apply current grader visibility state before restore.
                      // This prevents hidden grader annotations from reappearing after reload.
                      if (annotationsData?.annotations) {
                        annotationsData.annotations = annotationsData.annotations.filter((ann: any) => {
                          const annGraderId = ann.metadata?.graderId;
                          if (!annGraderId) {
                            return false;
                          }
                          const normalizedAnnGraderId = String(annGraderId);
                          return visibleGraders.get(normalizedAnnGraderId) !== false;
                        });
                      }
                      restoreCornerstoneAnnotations(annotationsData);
                    }, 1500);
                  }
                }
              }
      }
          }
        } catch (reloadError) {
          // Error reloading annotation data
        }
        setSaving(false);
        return true;
      }

      if (!options?.fromNavigation) {
        notification?.show?.({
          title: `Failed to ${status.toLowerCase()}`,
          message: response.error || 'Please try again',
          type: 'error',
          duration: 5000,
          position: 'top-right',
        });
      }
      setSaving(false);
      return false;
    } catch (error) {
      if (!options?.fromNavigation) {
        notification?.show?.({
          title: `Failed to ${status.toLowerCase()}`,
          message: 'An error occurred while saving the form',
          type: 'error',
          duration: 5000,
          position: 'top-right',
        });
      }
      setSaving(false);
      return false;
    }
  };

  const saveAnnotationRef = useRef(saveAnnotation);
  saveAnnotationRef.current = saveAnnotation;
  const studyUidRef = useRef(studyUid);
  studyUidRef.current = studyUid;

  useEffect(() => {
    const handler: SaveDraftHandler = async options => {
      const activeStudyUid = await getActiveViewerStudyUid();
      const formStudyUid = studyUidRef.current || null;
      const hasStaleFormStudyContext =
        activeStudyUid != null &&
        formStudyUid != null &&
        formStudyUid !== activeStudyUid;
      const globalMatchesActive = isAnnotationResponseForStudyUid(
        window.annotationsData,
        activeStudyUid
      );

      if (
        !protocol ||
        !annotationData ||
        !studyId ||
        hasStaleFormStudyContext ||
        !globalMatchesActive
      ) {
        const { saveDraftAnnotationsWithActivity } = await import(
          '../../../extensions/cornerstone/src/utils/annotationSaveUtils'
        );
        return saveDraftAnnotationsWithActivity();
      }
      return saveAnnotationRef.current('Draft', options);
    };

    floatingFormStore.registerSaveDraftHandler(handler);
    return () => floatingFormStore.unregisterSaveDraftHandler();
  }, [protocol, annotationData, studyId, studyUid]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isReadOnly || saving) {
      return;
    }

    // Check if no fields are filled - show warning similar to save draft
    if (!hasAtLeastOneField()) {
      setShowEmptyFormWarning(true);
      return;
    }

    // Calculate field counts for the confirmation message
    const totalFields = getTotalFieldsCount();
    const filledFields = getFilledFieldsCount();
    setFieldCountInfo({ filled: filledFields, total: totalFields });
    setMissingFields(getEmptyFields());
    setShowSubmitConfirmation(true);
  };

  const handleConfirmSubmit = async () => {
    setShowSubmitConfirmation(false);
    setFieldCountInfo(null);
    setMissingFields([]);
    await saveAnnotation('Submit');
  };

  const handleCancelSubmitConfirmation = () => {
    setShowSubmitConfirmation(false);
    setFieldCountInfo(null);
    setMissingFields([]);
  };

  // Check if at least one field has been entered
  const hasAtLeastOneField = () => {
    const formValues = Object.values(formData);
    return formValues.some(value => {
      if (Array.isArray(value)) {
        return value.length > 0 && value.some(v => v && String(v).trim() !== '');
      }
      return value !== null && value !== undefined && value !== '' && String(value).trim() !== '';
    });
  };

  // Count total fields from protocol
  const getTotalFieldsCount = (): number => {
    if (!protocol || !protocol.parameters) return 0;
    let count = 0;
    const countFields = (params: any[]) => {
      params.forEach(param => {
        if (param.parameter_fields && param.parameter_fields.length > 0) {
          count += param.parameter_fields.length;
        }
        if (param.children && param.children.length > 0) {
          countFields(param.children);
        }
      });
    };
    countFields(protocol.parameters);
    return count;
  };

  // Count filled fields from formData
  const getFilledFieldsCount = (): number => {
    const formValues = Object.values(formData);
    return formValues.filter(value => {
      if (Array.isArray(value)) {
        return value.length > 0 && value.some(v => v && String(v).trim() !== '');
      }
      return value !== null && value !== undefined && value !== '' && String(value).trim() !== '';
    }).length;
  };

  // Collect human-readable labels for fields that are still empty
  const getEmptyFields = (): string[] => {
    if (!protocol || !protocol.parameters) return [];
    const empty: string[] = [];

    const isValueEmpty = (value: any) => {
      if (Array.isArray(value)) {
        return value.length === 0 || value.every(v => !v || String(v).trim() === '');
      }
      return value === null || value === undefined || String(value).trim() === '';
    };

    const processFields = (fields: ParameterField[], baseKey: string, labelPrefix: string) => {
      fields.forEach(field => {
        const fieldKey = `${baseKey}_field_${field.id}`;
        const value = formData[fieldKey];

        if (isValueEmpty(value)) {
          empty.push(`${labelPrefix} - ${field.label}`);
        }
      });
    };

    const traverseParameters = (params: Parameter[], parentKey = '') => {
      params.forEach(param => {
        const key = parentKey ? `${parentKey}_${param.id}` : param.id.toString();
        const paramLabel = getParameterDisplayName(param);

        if (param.has_child && param.children.length > 0) {
          param.children.forEach(child => {
            const childKey = `${key}_${child.id}`;
            const childLabel = getParameterDisplayName(child);
            processFields(child.parameter_fields || [], childKey, `${paramLabel} / ${childLabel}`);
          });
        } else {
          processFields(param.parameter_fields || [], key, paramLabel);
        }
      });
    };

    traverseParameters(protocol.parameters);
    return empty;
  };

  const handleSaveDraft = async () => {
    // Check if at least one field has been entered
    if (!hasAtLeastOneField()) {
      setShowEmptyFormWarning(true);
      return;
    }
    // Show validation (missing fields) before saving draft, same as submit
    const totalFields = getTotalFieldsCount();
    const filledFields = getFilledFieldsCount();
    setFieldCountInfo({ filled: filledFields, total: totalFields });
    setMissingFields(getEmptyFields());
    setShowDraftConfirmation(true);
  };

  const handleConfirmSaveDraft = async () => {
    setShowDraftConfirmation(false);
    setFieldCountInfo(null);
    setMissingFields([]);
    await saveAnnotation('Draft');
  };

  const handleCancelSaveDraft = () => {
    setShowDraftConfirmation(false);
    setFieldCountInfo(null);
    setMissingFields([]);
  };

  const handleCloseEmptyFormWarning = () => {
    setShowEmptyFormWarning(false);
  };

  const handleStudyCompletionOkay = () => {
    setShowStudyCompletionModal(false);

    // Clear annotation data when returning to studies list (same as back button)
    console.log('[FloatingForm] Clearing annotation data on return to studies list');
    window.annotationsData = undefined;
    sessionStorage.removeItem('currentStudyUid');
    sessionStorage.removeItem('currentJobId');
    sessionStorage.removeItem('isViewOnly');

    // Trigger navigation through the store (will be handled by FloatingFormNavigationHandler)
    floatingFormStore.triggerNavigateToStudiesList();
  };

  const handleStudyCompletionStayHere = () => {
    setShowStudyCompletionModal(false);
    // Reload the page to refresh the DICOM viewer and reset timers
    window.location.reload();
  };

  const handleCancel = () => {
    onClose();
  };

  // Update form data
  const updateFormData = (fieldKey: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  // Helper to get numeric range bounds from a field
  const getFieldRangeBounds = (field: ParameterField): { min: number | null; max: number | null } => {
    const anyField = field as any;

    const range = anyField.range || {};
    const rawMin = range?.min ?? anyField.min;
    const rawMax = range?.max ?? anyField.max;

    const hasMin = rawMin !== undefined && rawMin !== null && rawMin !== '';
    const hasMax = rawMax !== undefined && rawMax !== null && rawMax !== '';

    const min = hasMin ? Number(rawMin) : null;
    const max = hasMax ? Number(rawMax) : null;

    return { min, max };
  };

  // Helper to format and extract numeric range text from a field
  const getFieldRangeText = (field: ParameterField): string | null => {
    const { min, max } = getFieldRangeBounds(field);

    const hasMin = min !== null && !Number.isNaN(min);
    const hasMax = max !== null && !Number.isNaN(max);

    if (!hasMin && !hasMax) {
      return null;
    }

    if (min !== null && max !== null) {
      return `${min} – ${max}`;
    }

    if (min !== null) {
      return `≥ ${min}`;
    }

    if (max !== null) {
      return `≤ ${max}`;
    }

    return null;
  };

  // Render input field based on type
  const renderInputField = (field: ParameterField, fieldKey: string) => {
    const value = formData[fieldKey] || '';

    // Range / validation helpers
    const { min, max } = getFieldRangeBounds(field);
    const hasMin = min !== null && !Number.isNaN(min);
    const hasMax = max !== null && !Number.isNaN(max);
    const numericValue =
      typeof value === 'string' && value.trim() !== '' ? Number(value) : null;

    let isOutOfRange = false;
    if (numericValue !== null && !Number.isNaN(numericValue) && (hasMin || hasMax)) {
      if ((hasMin && numericValue < (min as number)) || (hasMax && numericValue > (max as number))) {
        isOutOfRange = true;
      }
    }

    // Unit to show in warning text (same as type-based unit)
    const warningUnit: string | undefined = (field as any).type;
    let warningMessage: string | null = null;
    if (isOutOfRange) {
      const unitSuffix = warningUnit ? ` ${warningUnit}` : '';
      if (hasMin && hasMax) {
        warningMessage = `Value is outside the typical range (${min}–${max}${unitSuffix}).`;
      } else if (hasMin) {
        warningMessage = `Value is below the typical minimum (${min}${unitSuffix}).`;
      } else if (hasMax) {
        warningMessage = `Value is above the typical maximum (${max}${unitSuffix}).`;
      }
    }

    // Convert element_type to lowercase for consistent comparison
    const elementType = field.element_type?.toLowerCase();

    switch (elementType) {
      case 'textbox': {
        // Check if this should be a multiline textarea
        const isMultiline =
          field.label?.toLowerCase().includes('remarks') ||
          field.label?.toLowerCase().includes('notes') ||
          field.label?.toLowerCase().includes('description');

        // Check if this is a numeric textbox (label.toLowerCase === 'numeric' or has unit in type field)
        const isNumericTextBox = field.label?.toLowerCase() === 'numeric';

        if (isMultiline) {
          return (
            <textarea
              value={value}
              onChange={isReadOnly ? undefined : (e) => updateFormData(fieldKey, e.target.value)}
              placeholder={isReadOnly ? '' : `Enter ${field.label}`}
              rows={3}
              disabled={isReadOnly}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #3a3f99',
                borderRadius: '4px',
                fontSize: '14px',
                resize: 'vertical',
                backgroundColor: '#0b102f',
                color: isReadOnly ? '#ffffff' : '#ffffff',
                cursor: isReadOnly ? 'not-allowed' : 'text',
              }}
            />
          );
        }

        // Single line text input
        const numericInputHandlers = isNumericTextBox
          ? createNumericDecimalInputHandlers(nextValue => updateFormData(fieldKey, nextValue))
          : null;

        const inputElement = (
          <input
            type="text"
            inputMode={isNumericTextBox ? 'decimal' : undefined}
            value={value}
            onChange={
              isReadOnly
                ? undefined
                : numericInputHandlers
                  ? numericInputHandlers.onChange
                  : (e) => updateFormData(fieldKey, e.target.value)
            }
            onKeyDown={isReadOnly ? undefined : numericInputHandlers?.onKeyDown}
            onPaste={isReadOnly ? undefined : numericInputHandlers?.onPaste}
            placeholder={isReadOnly ? '' : `Enter ${field.label}`}
            disabled={isReadOnly}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #3a3f99',
              borderRadius: '4px',
              fontSize: '14px',
              backgroundColor: '#0b102f',
              color: isReadOnly ? '#ffffff' : '#ffffff',
              cursor: isReadOnly ? 'not-allowed' : 'text',
            }}
          />
        );

        // Only show warning for numeric text boxes
        if (isNumericTextBox && warningMessage) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {inputElement}
              <span
                style={{
                  color: '#facc15',
                  fontSize: '11px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <span
                  style={{
                    fontSize: '14px',
                    lineHeight: 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  ⚠
                </span>
                <span>{warningMessage}</span>
              </span>
            </div>
          );
        }

        return inputElement;
      }

      case 'numeric': {
        // type="text" + regex: blocks exponent (e/E) and Firefox allowing letters in type="number"
        const numericFieldHandlers = createNumericDecimalInputHandlers(nextValue =>
          updateFormData(fieldKey, nextValue)
        );
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="text"
                inputMode="decimal"
                value={value}
                onChange={isReadOnly ? undefined : numericFieldHandlers.onChange}
                onKeyDown={isReadOnly ? undefined : numericFieldHandlers.onKeyDown}
                onPaste={isReadOnly ? undefined : numericFieldHandlers.onPaste}
                onWheel={e => {
                  // Completely disable mouse wheel changes on this numeric field.
                  // Blur the input to prevent browsers from applying wheel increments.
                  e.preventDefault();
                  e.stopPropagation();
                  if (document.activeElement === e.currentTarget) {
                    (e.currentTarget as HTMLInputElement).blur();
                  }
                }}
                placeholder={isReadOnly ? '' : `Enter ${field.label}`}
                disabled={isReadOnly}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: '1px solid #3a3f99',
                  borderRadius: '4px',
                  fontSize: '14px',
                  backgroundColor: '#0b102f',
                  color: isReadOnly ? '#ffffff' : '#ffffff',
                  cursor: isReadOnly ? 'not-allowed' : 'text',
                }}
              />
              {field.label?.toUpperCase().includes('thickness') && (
                <span style={{ color: '#9ca3af', fontSize: '12px' }}>μm</span>
              )}
            </div>
            {warningMessage && (
              <span
                style={{
                  color: '#facc15',
                  fontSize: '11px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <span
                  style={{
                    fontSize: '14px',
                    lineHeight: 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  ⚠
                </span>
                <span>{warningMessage}</span>
              </span>
            )}
          </div>
        );
      }

      case 'dropdown':
      return (
        <select
          value={value}
            onChange={isReadOnly ? undefined : (e) => updateFormData(fieldKey, e.target.value)}
            disabled={isReadOnly}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #3a3f99',
            borderRadius: '4px',
            fontSize: '14px',
            backgroundColor: '#0b102f',
                  color: isReadOnly ? '#ffffff' : '#ffffff',
              cursor: isReadOnly ? 'not-allowed' : 'pointer',
          }}
        >
            <option value="">Select {field.label}</option>
          {field.values?.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );

      case 'checkbox':
      const selectedValues = Array.isArray(value) ? value : [];
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 16px' }}>
          {field.values?.map((option) => (
            <label
              key={option}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                  color: isReadOnly ? '#ffffff' : '#ffffff',
                fontSize: '14px',
                  cursor: isReadOnly ? 'not-allowed' : 'pointer',
                margin: 0,
              }}
            >
              <input
                type="checkbox"
                checked={selectedValues.includes(option)}
                  onChange={isReadOnly ? undefined : (e) => {
                  const newValues = e.target.checked
                    ? [...selectedValues, option]
                    : selectedValues.filter((v) => v !== option);
                  updateFormData(fieldKey, newValues);
                }}
                  disabled={isReadOnly}
                  style={{
                    width: '16px',
                    height: '16px',
                    cursor: isReadOnly ? 'not-allowed' : 'pointer',
                    accentColor: '#5BCDE6',
                  }}
                />
                <span>{option}</span>
            </label>
          ))}
        </div>
      );

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => updateFormData(fieldKey, e.target.value)}
            placeholder={`Enter ${field.label}`}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #3a3f99',
              borderRadius: '4px',
              fontSize: '14px',
              backgroundColor: '#0b102f',
              color: '#ffffff',
            }}
          />
        );
    }
  };

  // Render input fields from parameter_fields array
  const renderParameterFields = (fields: ParameterField[], baseKey: string) => {
    if (!fields || fields.length === 0) {
      return <p style={{ color: '#9ca3af', fontSize: '14px' }}>No fields defined</p>;
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {fields?.map((field) => {
          const fieldKey = `${baseKey}_field_${field.id}`;

          const apiLabelLower = String(field.label ?? '').toLowerCase();
          // Numeric label behavior in the UI is determined by API label text.
          const isNumeric = apiLabelLower === 'numeric';

          // Check for unit in type field or try to extract from label
          let unit = field.type;
          // if (!unit && isNumeric) {
          //   // Try to extract unit from label if it contains parentheses
          //   const unitMatch = field.label.match(/\(([^)]+)\)$/);
          //   if (unitMatch) {
          //     unit = unitMatch[1];
          //   }
          // }

          // Range text for numeric fields, if available
          const baseRangeText = isNumeric ? getFieldRangeText(field) : null;
          const rangeText =
            baseRangeText && unit
              ? `Range: ${baseRangeText} ${unit}`
              : baseRangeText
                ? `Range: ${baseRangeText}`
                : null;

          const baseDisplayLabel = getFieldDisplayLabel(field);
          const displayLabel =
            isNumeric && unit
              ? `${baseDisplayLabel} (${unit})`
              : baseDisplayLabel;

          return (
            <div key={field.id}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '8px',
                  gap: '8px',
                }}
              >
                <label
                  style={{
                    fontWeight: 500,
                    color: '#ffffff',
                    fontSize: '13px',
                  }}
                >
                  {displayLabel}
                </label>
                {rangeText && (
                  <span
                    style={{
                      fontSize: '11px',
                      color: '#9ca3af',
                      textAlign: 'right',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {rangeText}
                  </span>
                )}
              </div>
              {renderInputField(field, fieldKey)}
            </div>
          );
        })}
      </div>
    );
  };

  // Render a child parameter with its fields
  const renderChildParameter = (child: Parameter, parentKey: string) => {
    const childKey = `${parentKey}_${child.id}`;

    return (
      <div key={child.id} style={{ marginBottom: '16px' }}>
          <h5 style={{
            margin: '0 0 8px 0',
            fontSize: '14px',
            fontWeight: 700,
            color: '#e3e3e3',
          }}>
          {getParameterDisplayName(child)}
          </h5>
        {renderParameterFields(child.parameter_fields || [], childKey)}
      </div>
    );
  };

  // Render parameter based on has_child flag
  const renderParameter = (param: Parameter, parentKey = '') => {
    const key = parentKey ? `${parentKey}_${param.id}` : param.id.toString();

    return (
      <div key={param.id} style={{ marginBottom: '20px', marginTop: '20px' }}>
        <div style={{ position: 'relative', marginTop: '16px' }}>
          {/* Parameter name as border badge */}
          <div style={{
            position: 'absolute',
            top: '-10px',
            left: '20px',
            background: 'transparent',
            color: 'rgb(46 204 239)',
            padding: '2px 6px',
            border: 'none',
            borderRadius: '14px',
            fontSize: '12px',
            fontWeight: 700,
            whiteSpace: 'nowrap',
            backgroundColor: '#14183a',
          }}>
            {getParameterDisplayName(param)}
          </div>

          {/* Container with border */}
              <div style={{
            background: '#14183A',
                border: '1px solid #3a3f99',
                borderRadius: '10px',
                padding: '12px 7px'
              }}>
            {param.has_child ? (
              // If has_child is true, show children with their parameter fields
              <div>
                {param.children?.map((child) => renderChildParameter(child, key))}
              </div>
            ) : (
              // If has_child is false, directly show parameter fields
              <div style={{ padding: '4px' }}>
                {renderParameterFields(param.parameter_fields || [], key)}
          </div>
        )}
          </div>
        </div>
      </div>
    );
  };

  // Don't render form if not visible and modal is not showing
  if (!isVisible && !showStudyCompletionModal) return null;

  const headerTitle = protocol ? protocol.name : 'Retinal Grading';

  return (
    <>
      {isVisible && (
        <div
          ref={formRef}
          className="floating-grading-form"
          style={{
            position: 'fixed',
            top: position.y,
            left: position.x,
            width: '440px',
            maxHeight: 'calc(100vh - 50px)',
            height: 'calc(100vh - 50px)',
            backgroundColor: '#090C2A',
            border: '1px solid rgb(58 63 153)',
            borderRadius: '10px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            zIndex: 9999,
            fontFamily: 'Arial, sans-serif',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .floating-grading-form input::placeholder,
        .floating-grading-form textarea::placeholder {
          color: #8ea0c8;
          opacity: 0.65;
        }
      `}</style>
      <div
        style={{
          // backgroundColor: '#071d4f',
          color: 'white',
          padding: '12px 20px',
          borderRadius: '9px 9px 0 0',
          cursor: 'move',
          display: 'flex',
          justifyContent: 'space-between',
        alignItems: 'flex-start',
          borderBottom: '1px solid rgb(58 63 153)',
          flexShrink: 0,
        }}
        onMouseDown={handleMouseDown}
      >
      <TooltipProvider>
        <AppTooltip content={headerTitle} side="bottom" align="start" sideOffset={10} maxWidth={360}>
          <h3
            style={{
              margin: 0,
              fontSize: '16px',
              fontWeight: 700,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              textOverflow: 'ellipsis',
              lineHeight: '1.4',
              maxHeight: '2.8em',
              flex: 1,
              marginRight: '12px',
              paddingTop: '2px',
            }}
          >
            {headerTitle}
          </h3>
        </AppTooltip>
      </TooltipProvider>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            color: 'white',
            fontSize: '18px',
            cursor: 'pointer',
            padding: '0',
            width: '24px',
            height: '24px',
            display: 'flex',
          alignItems: 'flex-start',
            justifyContent: 'center',
            flexShrink: 0,
            paddingTop: '2px',
          }}
        >
          ×
        </button>
      </div>

      <form
        onSubmit={handleSubmit}
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          backgroundColor: '#090C2A',
        }}
      >
        {/* Scrollable Content Area */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            paddingTop: '0px',
            paddingBottom: '10px',
            paddingLeft: '10px',
            paddingRight: '10px',
            backgroundColor: '#090C2A',
            minHeight: 0,
          }}
        >
          {loading ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '200px',
              color: '#ffffff'
            }}>
              Loading protocol...
            </div>
          ) : protocol ? (
            <>
              {/* View-only message above parameters */}
              {isViewOnlyUser && (
                <div
                  style={{
                    marginTop: '2px',
                    marginBottom: '2px',
                    padding: '5px 4px 0px 4px',
                    color: '#ef4444',
                    fontSize: '11px',
                    lineHeight: 1.4,
                    fontWeight: 600,
                  }}
                >
                  * You don&apos;t have access to make changes here.
                </div>
              )}
              {/* Loop through all parameters in protocols[0].parameters */}
              {protocol.parameters?.map(param => renderParameter(param))}
            </>
          ) : (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '200px',
              color: '#ffffff'
            }}>
              No protocol data available
            </div>
          )}
          </div>

        {/* Fixed Footer with Buttons */}
        <div
          style={{
            padding: '16px',
            borderTop: '1px solid #3a3f99',
            backgroundColor: '#041c4a',
            display: 'flex',
            justifyContent: 'end',
            flexShrink: 0,
            minHeight: '65px',
          }}
        >
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="button"
              onClick={handleCancel}
              style={{
                padding: '5px 12px',
                backgroundColor: 'transparent',
                color: 'white',
                border: '1px solid #3a3f99',
                borderRadius: '7px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
              onMouseOver={e => {
                (e.currentTarget as HTMLButtonElement).style.background = '#0a2a5a';
                (e.currentTarget as HTMLButtonElement).style.borderColor = '#1454D4';
              }}
              onMouseOut={e => {
                (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                (e.currentTarget as HTMLButtonElement).style.borderColor = '#3a3f99';
              }}
            >
              Cancel
            </button>
            {!isReadOnly && (
              <>
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={saving}
              style={{
                padding: '5px 12px',
                backgroundColor: saving ? '#666' : '#0a7ea4',
                color: 'white',
                border: 'none',
                borderRadius: '7px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              Save Draft
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: '5px 12px',
                backgroundColor: saving ? '#666' : '#1454d4',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              Submit
            </button>
              </>
            )}
          </div>
        </div>
      </form>

      {/* Full-page loader overlay */}
      {saving && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10002,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img src={loadingGif} alt="Loading..." style={{ width: '80px', height: '80px', objectFit: 'contain' }} />
          </div>
        </div>
      )}

        {showSubmitConfirmation && (
          <div
            role="dialog"
            aria-modal="true"
            style={confirmationModalStyles.overlay}
          >
            <div style={confirmationModalStyles.content}>
              <div style={confirmationModalStyles.header}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#ffffff' }}>Confirm Submit</h3>
                <button
                  type="button"
                  onClick={handleCancelSubmitConfirmation}
                  style={confirmationModalStyles.closeButton}
                  aria-label="Close confirmation"
                >
                  ×
                </button>
              </div>
              <div style={confirmationModalStyles.body}>
                {fieldCountInfo && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {missingFields.length > 0 && (
                      <div style={{ color: '#d1d5db', fontSize: '13px', lineHeight: 1.5 }}>
                        <p style={{ margin: '0 0 6px 0' }}>
                          You have not filled the following fields:
                        </p>
                        <div style={{ marginLeft: '12px' }}>
                          {missingFields.map(field => (
                            <div key={field} style={{ marginBottom: '4px' }}>
                              {field}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <p style={{ margin: 0 }}>Once you submit, this annotation cannot be changed. Do you want to continue?</p>
                  </div>
                )}
              </div>
              <div style={confirmationModalStyles.footer}>
                <button
                  type="button"
                  onClick={handleCancelSubmitConfirmation}
                  style={confirmationModalStyles.buttonSecondary}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmSubmit}
                  style={{
                    ...confirmationModalStyles.buttonPrimary,
                    backgroundColor: saving ? '#666' : confirmationModalStyles.buttonPrimary.backgroundColor,
                    cursor: saving ? 'not-allowed' : 'pointer',
                  }}
                  disabled={saving}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}

        {showDraftConfirmation && (
          <div
            role="dialog"
            aria-modal="true"
            style={confirmationModalStyles.overlay}
          >
            <div style={confirmationModalStyles.content}>
              <div style={confirmationModalStyles.header}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#ffffff' }}>Save Draft</h3>
                <button
                  type="button"
                  onClick={handleCancelSaveDraft}
                  style={confirmationModalStyles.closeButton}
                  aria-label="Close draft confirmation"
                >
                  ×
                </button>
              </div>
              <div style={confirmationModalStyles.body}>
                {fieldCountInfo && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {missingFields.length > 0 && (
                      <div style={{ color: '#d1d5db', fontSize: '13px', lineHeight: 1.5 }}>
                        <p style={{ margin: '0 0 6px 0' }}>
                          You have not filled the following fields:
                        </p>
                        <div style={{ marginLeft: '12px' }}>
                          {missingFields.map(field => (
                            <div key={field} style={{ marginBottom: '4px' }}>
                              {field}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <p style={{ margin: 0 }}>
                      {missingFields.length > 0
                        ? 'Do you want to save as draft anyway? You can complete the remaining fields later.'
                        : 'Do you want to save as draft?'}
                    </p>
                  </div>
                )}
              </div>
              <div style={confirmationModalStyles.footer}>
                <button
                  type="button"
                  onClick={handleCancelSaveDraft}
                  style={confirmationModalStyles.buttonSecondary}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmSaveDraft}
                  style={{
                    ...confirmationModalStyles.buttonPrimary,
                    backgroundColor: saving ? '#666' : confirmationModalStyles.buttonPrimary.backgroundColor,
                    cursor: saving ? 'not-allowed' : 'pointer',
                  }}
                  disabled={saving}
                >
                  Save draft
                </button>
              </div>
            </div>
          </div>
        )}

        {showEmptyFormWarning && (
          <div
            role="dialog"
            aria-modal="true"
            style={confirmationModalStyles.overlay}
          >
            <div style={confirmationModalStyles.content}>
              <div style={confirmationModalStyles.header}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#ffffff' }}>Warning</h3>
                <button
                  type="button"
                  onClick={handleCloseEmptyFormWarning}
                  style={confirmationModalStyles.closeButton}
                  aria-label="Close warning"
                >
                  ×
                </button>
              </div>
              <div style={confirmationModalStyles.body}>
                <p style={{ margin: 0 }}>
                Please add some information before saving.
                </p>
              </div>
              <div style={confirmationModalStyles.footer}>
                <button
                  type="button"
                  onClick={handleCloseEmptyFormWarning}
                  style={confirmationModalStyles.buttonPrimary}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Study completion modal - rendered outside form div so it shows even after form closes */}
      {showStudyCompletionModal && (
        <div
          role="dialog"
          aria-modal="true"
          style={confirmationModalStyles.overlay}
        >
          <div style={confirmationModalStyles.content}>
            <div style={confirmationModalStyles.header}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#ffffff' }}>Study Completed</h3>
              <button
                type="button"
                onClick={handleStudyCompletionOkay}
                style={confirmationModalStyles.closeButton}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div style={confirmationModalStyles.body}>
              <p style={{ margin: 0, marginBottom: '12px', color: '#d1d5db', fontSize: '14px', lineHeight: 1.6 }}>
                Your study has been successfully completed and submitted. All annotations and grading data have been saved and are ready for review.
              </p>
              <p style={{ margin: 0, color: '#d1d5db', fontSize: '14px', lineHeight: 1.6 }}>
                Would you like to refresh the page to enter review mode and continue reviewing, or close this message and return to the study list?
              </p>
            </div>
            <div style={confirmationModalStyles.footer}>
              <button
                type="button"
                onClick={handleStudyCompletionStayHere}
                style={confirmationModalStyles.buttonSecondary}
              >
                Stay Here
              </button>
              <button
                type="button"
                onClick={handleStudyCompletionOkay}
                style={confirmationModalStyles.buttonPrimary}
              >
                Okay
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const UnsavedAnnotationsNavigationModal: React.FC = () => {
  const [modalState, setModalState] = useState(() => floatingFormStore.getUnsavedNavigationState());

  useEffect(() => {
    return floatingFormStore.subscribeToUnsavedNavigation(() => {
      setModalState(floatingFormStore.getUnsavedNavigationState());
    });
  }, []);

  if (!modalState.showModal && !modalState.isSaving) {
    return null;
  }

  if (modalState.isSaving) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10003,
        }}
        aria-live="polite"
        aria-busy="true"
      >
        <img
          src={loadingGif}
          alt="Saving annotations..."
          style={{ width: '80px', height: '80px', objectFit: 'contain' }}
        />
      </div>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={confirmationModalStyles.overlay}
    >
      <div style={confirmationModalStyles.content}>
        <div style={confirmationModalStyles.header}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#ffffff' }}>
            Unsaved Annotations
          </h3>
          <button
            type="button"
            onClick={() => floatingFormStore.cancelUnsavedNavigation()}
            style={confirmationModalStyles.closeButton}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div style={confirmationModalStyles.body}>
          <p style={{ margin: 0 }}>
            You have unsaved annotations. Would you like to save them before leaving?
          </p>
        </div>
        <div style={confirmationModalStyles.footer}>
          <button
            type="button"
            onClick={() => floatingFormStore.cancelUnsavedNavigation()}
            style={confirmationModalStyles.buttonSecondary}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => floatingFormStore.discardUnsavedNavigationAndProceed()}
            style={confirmationModalStyles.buttonSecondary}
          >
            Don&apos;t Save
          </button>
          <button
            type="button"
            onClick={() => floatingFormStore.confirmUnsavedNavigationSave()}
            style={confirmationModalStyles.buttonPrimary}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

/** Prompt before leaving the viewer when new unsaved annotations exist on the canvas. */
export const promptUnsavedAnnotationsBeforeNavigate = (
  onProceed: () => void | Promise<void>
): Promise<void> => floatingFormStore.promptNavigation(onProceed);

/** Save draft annotations + activity log (same as navigation confirmation Save). */
export const saveViewerDraftAnnotations = (): Promise<boolean> =>
  floatingFormStore.saveDraftAnnotations();

/** Close the floating grading form (e.g. before prev/next study navigation). */
export const hideFloatingGradingForm = (): void => {
  floatingFormStore.hideForm();
};

/** True when the open floating form has grading fields changed since load. */
export const hasUnsavedFormFieldChanges = (): boolean => {
  if (!floatingFormStore.getState().isVisible) {
    return false;
  }

  const baseline = (window as any).floatingFormDataBaseline as string | null | undefined;
  const current = (window as any).floatingFormDataCurrent as string | null | undefined;

  if (baseline == null || current == null) {
    return false;
  }

  return baseline !== current;
};

// Inner component that handles navigation tracking (must be inside Router context)
const FloatingFormNavigationTracker: React.FC = () => {
  const { isVisible, hideForm } = useFloatingFormStore();
  const location = useLocation();
  const previousPathRef = useRef<string>(location.pathname);
  const previousSearchRef = useRef<string>(location.search);
  const lastTrackedViewerStudyUidRef = useRef<string | null>(null);

  // Browser refresh / tab close — native confirmation when unsaved viewer work exists.
  useEffect(() => {
    if (!location.pathname.startsWith('/viewer')) {
      return;
    }

    return registerViewerBeforeUnloadGuard();
  }, [location.pathname]);

  // Close floating form when leaving the viewer or when prev/next study changes the URL study UID.
  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousPathRef.current;
    const previousSearch = previousSearchRef.current;

    const inViewer = currentPath.startsWith('/viewer');
    const wasInViewer = previousPath.startsWith('/viewer');
    const pathChanged = currentPath !== previousPath;

    const currentStudyUid = inViewer ? getViewerStudyUidFromSearch(location.search) : null;
    const previousStudyUid = wasInViewer ? getViewerStudyUidFromSearch(previousSearch) : null;
    const studyChangedInViewer =
      inViewer &&
      wasInViewer &&
      !pathChanged &&
      currentStudyUid != null &&
      previousStudyUid != null &&
      currentStudyUid !== previousStudyUid;

    if (inViewer && currentStudyUid) {
      const lastTrackedUid = lastTrackedViewerStudyUidRef.current;
      if (lastTrackedUid && lastTrackedUid !== currentStudyUid) {
        floatingFormStore.notifyStudyContextChanged(currentStudyUid);
      }
      lastTrackedViewerStudyUidRef.current = currentStudyUid;
    }

    if (pathChanged) {
      const leavingViewer = wasInViewer && !inViewer;

      if (leavingViewer) {
        ActivityService.getInstance()
          .logActivity('exit')
          .catch(() => {
            // Failed to log exit activity - non-blocking
          });
      }

      if (isVisible) {
        hideForm();
      }
    } else if (studyChangedInViewer) {
      if (isVisible) {
        hideForm();
      }
    }

    previousPathRef.current = currentPath;
    previousSearchRef.current = location.search;
  }, [location.pathname, location.search, isVisible, hideForm]);

  // Also handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = async () => {
      const newPath = window.location.pathname;
      const previousPath = previousPathRef.current;

      if (newPath !== previousPath) {
        const wasInViewer = previousPath.startsWith('/viewer');
        const leavingViewer = wasInViewer && !newPath.startsWith('/viewer');

        if (leavingViewer) {
          try {
            const activityService = ActivityService.getInstance();
            await activityService.logActivity('exit');
          } catch (error) {
            // Failed to log exit activity
          }
        }

        if (isVisible) {
          hideForm();
        }

        previousPathRef.current = newPath;
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isVisible, hideForm]);

  return null;
};

// Navigation handler component (must be inside Router context)
const FloatingFormNavigationHandler: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get extensionManager from system context (may be null if context not available)
  const system = useSystem();
  const extensionManager = system?.extensionManager || null;

  useEffect(() => {
    const navigateToStudiesList = () => {
      // Use the same navigation logic as the back button in ViewerHeader
      const { pathname } = location;
      const dataSourceIdx = pathname.indexOf('/', 1);

      const searchQuery = new URLSearchParams();
      if (dataSourceIdx !== -1 && extensionManager) {
        const dataSourceName = pathname.substring(dataSourceIdx + 1);
        const existingDataSource = extensionManager.getDataSources(dataSourceName);
        if (existingDataSource) {
          searchQuery.append('datasources', pathname.substring(dataSourceIdx + 1));
        }
      }

      preserveQueryParameters(searchQuery);

      navigate({
        pathname: '/studiesList',
        search: decodeURIComponent(searchQuery.toString()),
      });
    };

    const checkAndNavigate = () => {
      if (floatingFormStore.shouldNavigateToStudiesList()) {
        void promptUnsavedAnnotationsBeforeNavigate(navigateToStudiesList);
      }
    };

    // Subscribe to navigation triggers
    const unsubscribe = floatingFormStore.subscribeToNavigation(checkAndNavigate);

    // Also check immediately in case trigger happened before subscription
    checkAndNavigate();

    return unsubscribe;
  }, [navigate, location, extensionManager]);

  return null;
};

// Provider Component (can be outside Router context)
export const FloatingFormProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isVisible, instanceUid, hideForm } = useFloatingFormStore();

  return (
    <>
      <UnsavedAnnotationsNavigationModal />
      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
      {children}
      <FloatingGradingForm
        isVisible={isVisible}
        instanceUid={instanceUid}
        onClose={hideForm}
      />
    </>
  );
};

// Export the navigation tracker and handler to be used inside Router
export { FloatingFormNavigationTracker, FloatingFormNavigationHandler };

// Function to show the form (exported for use in commands)
export const showFloatingGradingForm = (instanceUid?: string) => {
  const studyUid =
    instanceUid ||
    (typeof window !== 'undefined' ? sessionStorage.getItem('currentStudyUid') : null) ||
    undefined;
  floatingFormStore.showForm(studyUid);
};


export const resetAnnotationLifecycleState = async () => {
  deletedAnnotationUIDs.clear();
  masterMergedAnnotations = null;
  visibleGraders.clear();
  graderIdToColorMap.clear();
  lastRestoredStudyUid = null;
  await clearAllCornerstoneAnnotations();
};

// Function to restore annotations from API response (exported for use in viewer init)
export const restoreCornerstoneAnnotationsFromData = async (apiResponse: any) => {
  try {

    const responseData = apiResponse as any;
    if (!responseData.is_success || !responseData.data || responseData.data.length === 0) {
      // Fresh API call with no annotations - clear all deletedAnnotationUIDs
      deletedAnnotationUIDs.clear();
      modifiedAnnotationUIDs.clear();

      // Clear all existing annotations from Cornerstone Tools state
      // This prevents annotations from previous studies from persisting when API returns empty array
      await clearAllCornerstoneAnnotations();

      return;
    }

    const data = responseData.data[0];
    const responseStudyUid =
      data?.study?.study_uid != null
        ? String(data.study.study_uid)
        : data?.instance_uid != null
          ? String(data.instance_uid)
          : null;

    if (responseStudyUid && responseStudyUid !== lastRestoredStudyUid) {
      await clearAllCornerstoneAnnotations();
      deletedAnnotationUIDs.clear();
      modifiedAnnotationUIDs.clear();
      lastRestoredStudyUid = responseStudyUid;
    }

    // Fresh API from viewer init: stop tracking "pending delete" only for annotations the server
    // no longer returns (persisted delete). Do NOT drop tracking when the UID is still in the API —
    // that includes unsaved local deletes and would cause deleted drawings to reappear.
    if (data?.annotations?.[0]?.data) {
      const dataObj = data.annotations[0].data;
      let annotationsData = null;

      if (dataObj?.rawdata) {
        annotationsData = dataObj.rawdata;
      } else if (dataObj?.annotations) {
        let parsed = typeof dataObj.annotations === 'string' ? JSON.parse(dataObj.annotations) : dataObj.annotations;
        if (parsed.annotations && typeof parsed.annotations === 'object' && parsed.annotations.annotations) {
          parsed = parsed.annotations;
        }
        annotationsData = parsed;
      }

      if (annotationsData?.annotations && Array.isArray(annotationsData.annotations)) {
        const apiAnnotationUIDs = new Set(
          annotationsData.annotations.map((ann: any) => ann.annotationUID).filter(Boolean)
        );

        const uidsResolvedOnServer = Array.from(deletedAnnotationUIDs).filter(
          uid => !apiAnnotationUIDs.has(uid)
        );
        uidsResolvedOnServer.forEach(uid => deletedAnnotationUIDs.delete(uid));
      }
    }

    // ---------------------------------------------------------------------
    // Build a combined annotations payload depending on the logged-in role.
    // For admin/manager: merge all graders' annotations for the study.
    // For other roles: preserve existing behaviour (first annotation only).
    // ---------------------------------------------------------------------

    // Helper to safely normalize a single annotation data object into
    // the `{ annotations: [...] }` structure we expect.
    const extractAnnotationsFromDataObj = (dataObj: any): any | null => {
      if (!dataObj) {
        return null;
      }

      let result: any = null;

      if (dataObj.rawdata) {
        // New format
        if (typeof dataObj.rawdata === 'string') {
          try {
            result = JSON.parse(dataObj.rawdata);
          } catch {
            result = null;
          }
        } else {
          result = dataObj.rawdata;
        }
      } else if (dataObj.annotations) {
        // Old format
        try {
          let parsed =
            typeof dataObj.annotations === 'string'
              ? JSON.parse(dataObj.annotations)
              : dataObj.annotations;

          // Handle double-nested structure (old saves)
          if (
            parsed?.annotations &&
            typeof parsed.annotations === 'object' &&
            parsed.annotations.annotations
          ) {
            parsed = parsed.annotations;
          }

          result = parsed;
        } catch {
          result = null;
        }
      } else if (typeof dataObj === 'string') {
        try {
          result = JSON.parse(dataObj);
        } catch {
          result = null;
        }
      } else {
        // Direct object
        result = dataObj;
      }

      if (!result || !Array.isArray(result.annotations)) {
        return null;
      }

      return result;
    };

    // Decide whether to merge all graders' annotations based on role
    let combinedAnnotationsData: any | null = null;
    let currentUserIdForRestore: string | null = null;
    try {
      const { default: AuthService } = await import(
        '../../../platform/app/src/services/AuthService'
      );
      const authService = AuthService.getInstance();
      const currentUser = authService.getCurrentUser();
      currentUserIdForRestore = currentUser?.id != null ? String(currentUser.id) : null;
      const role = currentUser?.role?.toString().toLowerCase();

      const shouldMergeAllGraders =
        role === 'admin' || role === 'manager';

      if (shouldMergeAllGraders && Array.isArray(data.annotations) && data.annotations.length > 0) {
        // Merge annotations from all grader entries
        const mergedAnnotations: any[] = [];

        // Stable color assignment per grader within this restore call.
        // Use the provided bright, colorblind-friendly palette for up to 7 graders.
        const graderColorMap: Record<string, string> = {};
        const colorPalette: string[] = [
          '#00E5FF', // Cyan
          '#FFD60A', // Yellow
          '#B5179E', // Purple
          '#3A86FF', // Blue
          '#FF8C00', // Orange
          '#00FF88', // Green
          '#FFFFFF', // White (fallback)
        ];
        let colorIndex = 0;

        const getColorForGraderId = (id: number | string | null | undefined): string => {
          const key = id != null ? String(id) : 'unknown';
          if (!graderColorMap[key]) {
            graderColorMap[key] = colorPalette[colorIndex % colorPalette.length];
            colorIndex += 1;
          }
          // Also store in global map for use when creating new annotations
          graderIdToColorMap.set(key, graderColorMap[key]);
          return graderColorMap[key];
        };

        // Import grader name formatter to get formatted names
        let getGraderDisplayNameSync: ((id: number | string | null | undefined) => string) | null = null;
        try {
          const formatterModule = await import('../../../platform/app/src/utils/graderNameFormatter');
          getGraderDisplayNameSync = formatterModule.getGraderDisplayNameSync;
        } catch (error) {
          // Formatter not available - will use fallback
        }

        data.annotations.forEach((annEntry: any) => {
          const extracted = extractAnnotationsFromDataObj(annEntry?.data);
          if (extracted && Array.isArray(extracted.annotations)) {
            const graderIdForEntry =
              annEntry?.grader_id ??
              (annEntry?.grader && (annEntry.grader.id || annEntry.grader.user_id));
            const graderColor = getColorForGraderId(graderIdForEntry);
            const graderName =
              annEntry?.grader?.username ||
              annEntry?.grader?.first_name ||
              '';

            // Get formatted grader name from session storage if formatter is available
            const formattedGraderName = getGraderDisplayNameSync
              ? getGraderDisplayNameSync(graderIdForEntry)
              : graderName;

            // console.log(`[restoreCornerstoneAnnotationsFromData] Processing annotations for grader:`, {
            //   graderId: graderIdForEntry,
            //   normalizedGraderId: graderIdForEntry != null ? String(graderIdForEntry) : null,
            //   graderName: formattedGraderName,
            //   graderColor,
            //   annotationCount: extracted.annotations.length,
            //   source: {
            //     grader_id: annEntry?.grader_id,
            //     grader_id_type: typeof annEntry?.grader_id,
            //     grader_id_value: annEntry?.grader_id,
            //     grader_id_string: annEntry?.grader_id != null ? String(annEntry?.grader_id) : null,
            //     grader_id_from_grader: annEntry?.grader?.id,
            //     grader_user_id: annEntry?.grader?.user_id,
            //   }
            // });

            extracted.annotations.forEach((ann: any) => {
              // Create a shallow clone so we don't mutate shared references
              // Ensure metadata and data objects exist even if they were undefined
              const clonedAnn: any = {
                ...ann,
                metadata: {
                  ...(ann.metadata || {}),
                  // Normalize graderId to string for consistent comparison
                  graderId: graderIdForEntry != null ? String(graderIdForEntry) : null,
                  graderName: formattedGraderName, // Use formatted name
                  graderDisplayName: formattedGraderName, // Also store as displayName for easy access
                  graderColor, // Always set grader color in metadata
                },
                data: {
                  ...(ann.data || {}),
                  // Always use grader color when merging annotations from multiple graders
                  // This ensures each grader's annotations are displayed in a distinct color
                  color: graderColor, // Always set grader color in data
                },
              };
              mergedAnnotations.push(clonedAnn);
            });
          }
        });

        if (mergedAnnotations.length > 0) {
          // Store master copy of all merged annotations (untouched)
          masterMergedAnnotations = JSON.parse(JSON.stringify(mergedAnnotations));

          // Initialize grader visibility without clobbering the user's current selections.
          // Previously we called `visibleGraders.clear()` here, which caused the grader
          // checkboxes to "reset" when the floating form reloaded/remeerged annotations.
          // Instead, we keep existing values (including `false`) and only default
          // newly-seen graders to `true`.
          mergedAnnotations.forEach((ann: any) => {
            const graderId = ann.metadata?.graderId;
            if (!graderId) {
              return;
            }
            const graderIdStr = String(graderId);
            if (!visibleGraders.has(graderIdStr)) {
              visibleGraders.set(graderIdStr, true);
            }
          });

          // Filter annotations based on visible graders
          const filteredAnnotations = mergedAnnotations.filter((ann: any) => {
            const graderId = ann.metadata?.graderId;
            if (!graderId) {
              // Hide annotations without graderId - we can't determine which grader they belong to
              console.warn('[restoreCornerstoneAnnotationsFromData] Annotation without graderId found, hiding it:', ann.annotationUID);
              return false;
            }
            // Only show annotations if grader is explicitly set to visible (true)
            // If not in map or set to false, hide the annotation
            const normalizedGraderId = String(graderId);
            const isVisible = visibleGraders.get(normalizedGraderId) === true;
            return isVisible;
          });

          combinedAnnotationsData = {
            annotations: filteredAnnotations,
            timestamp: new Date().toISOString(),
            // Flag so downstream restore logic knows to apply per-grader colors
            __useGraderColors: true,
          };
        }
      }
    } catch {
      // If anything fails (e.g., AuthService import), fall back to default behaviour
      combinedAnnotationsData = null;
    }

    // Fallback to original single-annotation behaviour when:
    // - role is not admin/manager
    // - or merging produced no usable annotations
    if (!combinedAnnotationsData) {
      // Check if we have annotation data to restore
      if (!data.annotations || data.annotations.length === 0) {
        // API response has data structure but no annotations - clear existing annotations
        await clearAllCornerstoneAnnotations();
        return;
      }

      const annotationEntry =
        currentUserIdForRestore != null
          ? data.annotations.find((annEntry: any) => {
              const entryGraderId =
                annEntry?.grader_id ??
                annEntry?.grader?.id ??
                annEntry?.grader?.user_id;
              return entryGraderId != null && String(entryGraderId) === currentUserIdForRestore;
            }) || data.annotations.find((annEntry: any) => annEntry?.data)
          : data.annotations.find((annEntry: any) => annEntry?.data);

      if (!annotationEntry?.data) {
        await clearAllCornerstoneAnnotations();
        return;
      }

      const dataObj = annotationEntry.data;
      combinedAnnotationsData = extractAnnotationsFromDataObj(dataObj);
      if (combinedAnnotationsData) {
        // Explicitly disable grader colors in the non-admin/manager path
        (combinedAnnotationsData as any).__useGraderColors = false;
      }
    }

    // If still nothing usable, clear and exit
    if (!combinedAnnotationsData || !combinedAnnotationsData.annotations || !combinedAnnotationsData.annotations.length) {
      await clearAllCornerstoneAnnotations();
      return;
    }

    const annotationsData = combinedAnnotationsData;

    // Restore annotations after ensuring viewport is ready
    if (annotationsData && annotationsData.annotations && annotationsData.annotations.length > 0) {
      // Filter out deleted annotations, ReferenceLines, and invalid annotations before restoring
      if (annotationsData?.annotations) {
        const beforeCount = annotationsData.annotations.length;
        annotationsData.annotations = annotationsData.annotations.filter((ann: any) => {
          // Filter out deleted annotations
          if (deletedAnnotationUIDs.has(ann.annotationUID)) {
            return false;
          }
          // Filter out ReferenceLines (helper/guide lines)
          if (ann.metadata?.toolName === 'ReferenceLines') {
            return false;
          }

          // Validate annotation structure based on tool type
          const toolName = ann.metadata?.toolName;

          // For PlanarFreehandROI and LivewireContour, ensure contour data exists
          if (toolName === 'PlanarFreehandROI' || toolName === 'LivewireContour') {
            if (!ann.data || !ann.data.contour) {
              return false;
            }

            // For PlanarFreehandROI and LivewireContour, ensure polyline exists
            if (!ann.data.contour.polyline) {
              return false;
            }
          }

          // Allow SplineROI annotations through here; they will be validated and
          // have their spline instance reconstructed later in restoreCornerstoneAnnotations.

          return true;
        });
        const afterCount = annotationsData.annotations.length;
      }

      // Wait for viewport to be ready before restoring
      const waitForViewport = async () => {
        let attempts = 0;
        const maxAttempts = 20; // Try for up to 10 seconds (20 * 500ms)

        while (attempts < maxAttempts) {
          try {
            const cornerstoneTools = window.cornerstoneTools || (await import('@cornerstonejs/tools')).default;
            if (cornerstoneTools) {
              const { getRenderingEngines } = await import('@cornerstonejs/core');
              const renderingEngines = getRenderingEngines();

              // Check if we have rendering engines and viewports
              if (renderingEngines && renderingEngines.length > 0) {
                const hasViewports = renderingEngines.some((engine: any) => {
                  const viewports = engine.getViewports();
                  return viewports && viewports.length > 0;
                });

                if (hasViewports) {
                  restoreCornerstoneAnnotations(annotationsData);
                  return;
                }
              }
            }
          } catch (error) {
            // Error checking viewport readiness
          }

          attempts++;
          await new Promise(resolve => setTimeout(resolve, 500));
        }

        // If viewport still not ready after max attempts, try restoring anyway
        restoreCornerstoneAnnotations(annotationsData);
      };

      waitForViewport();
    } else {
      // Clear existing annotations since API indicates no annotations should be present
      await clearAllCornerstoneAnnotations();
    }
  } catch (error) {
      // Error in restoreCornerstoneAnnotationsFromData
  }
};

// Function to toggle grader visibility and re-apply annotation filtering
export const toggleGraderVisibility = async (graderId: string, isVisible: boolean) => {
  try {
    // Normalize graderId to string for consistent comparison
    const normalizedGraderId = String(graderId);

    // Update visibility state
    visibleGraders.set(normalizedGraderId, isVisible);

    // If we don't have master annotations, nothing to filter
    if (!masterMergedAnnotations || masterMergedAnnotations.length === 0) {
      return;
    }

    // Filter annotations based on visible graders
    const filteredAnnotations = masterMergedAnnotations.filter((ann: any) => {
      const annGraderId = ann.metadata?.graderId;
      const annGraderColor = ann.metadata?.graderColor || ann.data?.color;
      if (!annGraderId) {
        // Hide annotations without graderId - we can't determine which grader they belong to
        console.warn('[toggleGraderVisibility] Annotation without graderId found, hiding it:', ann.annotationUID);
        return false;
      }
      // Normalize both IDs to strings for consistent comparison
      const normalizedAnnGraderId = String(annGraderId);
      // Only hide annotations if the grader is explicitly set to hidden (false).
      // If graderId is missing from the map (e.g., drafts for a grader with no saved annotations),
      // we treat it as visible.
      const isVisible = visibleGraders.get(normalizedAnnGraderId) !== false;
      if (!isVisible) {
        console.log(`[toggleGraderVisibility] Hiding annotation from grader "${normalizedAnnGraderId}" (annotationUID: ${ann.annotationUID}, color: ${annGraderColor})`);
      } else {
        console.log(`[toggleGraderVisibility] Keeping annotation from grader "${normalizedAnnGraderId}" (annotationUID: ${ann.annotationUID}, color: ${annGraderColor})`);
      }
      return isVisible;
    });

    // Debug: Log filtering details
    console.log(`[toggleGraderVisibility] Toggling grader "${normalizedGraderId}" to ${isVisible ? 'visible' : 'hidden'}`);
    console.log(`[toggleGraderVisibility] Master annotations: ${masterMergedAnnotations.length}, After filtering: ${filteredAnnotations.length}`);

    // Capture any draft (unsaved) annotations currently present on canvas.
    // `masterMergedAnnotations` only comes from server-loaded merged data, so user-created
    // annotations that haven't been saved yet won't be present there. When we clear and
    // restore during visibility toggles, those drafts would otherwise be lost.
    let draftAnnotations: any[] = [];
    try {
      const cornerstoneTools =
        window.cornerstoneTools || (await import('@cornerstonejs/tools')).default;
      const currentAnnotations =
        cornerstoneTools?.annotation?.state?.getAllAnnotations?.() || [];

      const fallbackGraderId = (window as any).currentUserGraderId || currentUserGraderId;

      draftAnnotations = currentAnnotations.filter((ann: any) => {
        if (!ann?.annotationUID) {
          return false;
        }
        if (deletedAnnotationUIDs.has(ann.annotationUID)) {
          return false;
        }
        if (ann.metadata?.toolName === 'ReferenceLines') {
          return false;
        }

        // Prefer explicit graderId; fall back to current user's graderId.
        let annGraderId = ann.metadata?.graderId;
        if (!annGraderId && fallbackGraderId != null) {
          annGraderId = fallbackGraderId;
        }

        // If we still don't know draft ownership, keep it during toggle.
        if (!annGraderId) {
          return true;
        }

        const normalizedAnnGraderId = String(annGraderId);
        // Hide only when explicitly set to hidden.
        return visibleGraders.get(normalizedAnnGraderId) !== false;
      });
    } catch {
      // Ignore draft-capture failures; we can still restore the server snapshot.
    }

    const filteredUIDs = new Set(filteredAnnotations.map((a: any) => String(a.annotationUID)));
    const uniqueDraftAnnotations = draftAnnotations.filter((a: any) => !filteredUIDs.has(String(a.annotationUID)));

    // Mark draft annotations so restore logic can avoid injecting the grader legend marker.
    // Drafts are annotations that exist on canvas but aren't part of `masterMergedAnnotations` (server snapshot).
    const markedDraftAnnotations = uniqueDraftAnnotations.map((ann: any) => ({
      ...ann,
      metadata: {
        ...(ann.metadata || {}),
        __isDraft: true,
      },
      data: {
        ...(ann.data || {}),
      },
    }));

    // If a draft UID was previously (incorrectly) marked as deleted due to earlier temporary clears,
    // make sure it can be restored.
    markedDraftAnnotations.forEach((ann: any) => {
      if (ann?.annotationUID) {
        deletedAnnotationUIDs.delete(ann.annotationUID);
      }
    });

    const annotationsToRestore = [...filteredAnnotations, ...markedDraftAnnotations];

    // Show which graderIds are in the master annotations
    const uniqueGraderIds = new Set(masterMergedAnnotations.map(ann => String(ann.metadata?.graderId || 'none')));
    console.log(`[toggleGraderVisibility] Unique graderIds in master:`, Array.from(uniqueGraderIds));
    console.log(`[toggleGraderVisibility] Visibility map:`, Array.from(visibleGraders.entries()));

    // Log detailed breakdown of annotations by grader before filtering
    const annotationsByGrader = new Map<string, Array<{ uid: string; color: string; name: string }>>();
    masterMergedAnnotations.forEach((ann: any) => {
      const gId = String(ann.metadata?.graderId || 'none');
      const gColor = ann.metadata?.graderColor || ann.data?.color || 'no-color';
      const gName = ann.metadata?.graderName || ann.metadata?.graderDisplayName || 'unknown';
      if (!annotationsByGrader.has(gId)) {
        annotationsByGrader.set(gId, []);
      }
      annotationsByGrader.get(gId)!.push({ uid: ann.annotationUID, color: gColor, name: gName });
    });
    console.log(`[toggleGraderVisibility] Master annotations breakdown by grader:`,
      Array.from(annotationsByGrader.entries()).map(([gId, anns]) => ({
        graderId: gId,
        count: anns.length,
        sample: anns.slice(0, 3).map(a => ({ uid: a.uid, color: a.color, name: a.name }))
      }))
    );

    // Log details about filtered annotations for debugging
    const filteredByGrader = new Map<string, number>();
    filteredAnnotations.forEach((ann: any) => {
      const gId = String(ann.metadata?.graderId || 'none');
      filteredByGrader.set(gId, (filteredByGrader.get(gId) || 0) + 1);
    });
    console.log(`[toggleGraderVisibility] Filtered annotations by grader:`, Array.from(filteredByGrader.entries()));

    // Clear all existing annotations (but preserve master copy for filtering)
    suppressAnnotationRemovalTracking = true;
    suppressAnnotationModificationTracking = true;
    try {
      await clearAllCornerstoneAnnotations(true);

      // Wait until Cornerstone annotation state is fully cleared.
      // This avoids restore skipping drafts due to duplicate UID detection.
      const clearWait = async () => {
        try {
          const cornerstoneTools =
            window.cornerstoneTools || (await import('@cornerstonejs/tools')).default;
          const maxChecks = 10; // ~1s total
          for (let i = 0; i < maxChecks; i++) {
            const anns =
              cornerstoneTools?.annotation?.state?.getAllAnnotations?.() || [];
            if (anns.length === 0) {
              return;
            }
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        } catch {
          // If checking fails, proceed with restore anyway.
        }
      };

      await clearWait();


      // Restore filtered annotations (or clear if none should be visible)
      if (annotationsToRestore.length > 0) {
        const annotationsData = {
          annotations: annotationsToRestore,
          timestamp: new Date().toISOString(),
          __useGraderColors: true,
        };

        console.log(
          `[toggleGraderVisibility] Restoring ${annotationsToRestore.length} annotations (filtered + drafts)`
        );

               // Wait for viewport to be ready before restoring
        const waitForViewport = async () => {
          let attempts = 0;
          const maxAttempts = 20; // Try for up to 10 seconds (20 * 500ms)

          while (attempts < maxAttempts) {
            try {
              const cornerstoneTools =
                window.cornerstoneTools || (await import('@cornerstonejs/tools')).default;
              if (cornerstoneTools) {
                const { getRenderingEngines } = await import('@cornerstonejs/core');
                const renderingEngines = getRenderingEngines();

                // Check if we have rendering engines and viewports
                if (renderingEngines && renderingEngines.length > 0) {
                  const hasViewports = renderingEngines.some((engine: any) => {
                    const viewports = engine.getViewports();
                    return viewports && viewports.length > 0;
                  });

                  if (hasViewports) {
                    await restoreCornerstoneAnnotations(annotationsData);
                    // Force render after restoration
                    renderingEngines.forEach((engine: any) => {
                      const viewports = engine.getViewports();
                      viewports.forEach((viewport: any) => {
                        viewport.render();
                      });
                    });
                    return;
                  }
                }
              }
          } catch (error) {
            // Error checking viewport readiness
          }

          attempts++;
          await new Promise(resolve => setTimeout(resolve, 500));
        }

        // If viewport still not ready after max attempts, try restoring anyway
        await restoreCornerstoneAnnotations(annotationsData);
        // Force render after restoration
        try {
          const { getRenderingEngines } = await import('@cornerstonejs/core');
          const renderingEngines = getRenderingEngines();
          if (renderingEngines && renderingEngines.length > 0) {
            renderingEngines.forEach((engine: any) => {
              const viewports = engine.getViewports();
              viewports.forEach((viewport: any) => {
                viewport.render();
              });
            });
          }
        } catch (error) {
          // Error triggering render
        }
      };

      await waitForViewport();
    } else {
      // No annotations should be visible - ensure viewport is cleared
      console.log(`[toggleGraderVisibility] No annotations to restore - viewport should be clear`);
      try {
        const { getRenderingEngines } = await import('@cornerstonejs/core');
        const renderingEngines = getRenderingEngines();
        if (renderingEngines && renderingEngines.length > 0) {
          renderingEngines.forEach((engine: any) => {
            const viewports = engine.getViewports();
            viewports.forEach((viewport: any) => {
              viewport.render();
            });
          });
        }
      } catch (error) {
        // Error triggering render
      }
    }
  } finally {
    suppressAnnotationRemovalTracking = false;
    suppressAnnotationModificationTracking = false;
  }
  } catch (error) {
    console.error('Error toggling grader visibility:', error);
  }
};

// Function to get current visibility state for all graders
export const getGraderVisibilityState = (): Map<string, boolean> => {
  return new Map(visibleGraders);
};

// Function to get master annotations count (for debugging/info)
export const getMasterAnnotationsCount = (): number => {
  return masterMergedAnnotations ? masterMergedAnnotations.length : 0;
};
